import React, { useState, useRef, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextAWS';
import leagueApiService from '../../services/leagueApi';
import TeamLogo from '../../components/TeamLogo';
import { generateRandomSchedule, calculateMergedWeeks, getMatchupDateRange, getLastRegularSeasonWeek, roundDraftTimeTo5Min, getDraftTimeParts, formatDraftTime, DRAFT_TIME_MINUTE_OPTIONS } from '../../utils/scheduleUtils';
import {
  CogIcon,
  UsersIcon,
  CalendarIcon,
  ClipboardIcon,
  TargetIcon,
  RefreshIcon,
  CrownIcon,
  DiceIcon,
  WarningIcon,
  CheckIcon,
  CopyIcon,
  LinkIcon,
  PlusIcon,
  MailIcon,
  TrophyIcon,
  ChartIcon,
  InfoIcon,
  SubtractIcon
} from '../../components/icons/StreamlineFlexSolid';

const SCORING_TYPE_DESCRIPTIONS = {
  'Rotisserie': 'Fantasy team scores are based on where they rank amongst all other fantasy teams in each selected scoring category.',
  'Points-based': 'Fantasy team scores are based on an accumulation of pre-determined points for each selected scoring category.',
  'Head-to-Head Points': 'Fantasy teams compete against each other 1-on-1 and receive a Win, Loss or Tie each Scoring Period, depending on who has the most fantasy points.',
  'Head-to-Head Most Categories': 'Fantasy teams compete against each other 1-on-1 and receive a Win, Loss or Tie each Scoring Period, depending on which team leads in more scoring categories.',
  'Head-to-Head Each Category': 'Fantasy teams compete against each other 1-on-1 and receive a Win, Loss or Tie for each scoring category in each Scoring Period.',
  'Rotisserie Regular Season': 'Fantasy team scores are based on where they rank amongst all other fantasy teams in each selected scoring category (regular season only).'
};

function LeagueSettings() {
  const { league, setLeague } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [pendingTab, setPendingTab] = useState(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  
  // Track changes per tab
  const [tabChanges, setTabChanges] = useState({
    general: {},
    members: {},
    schedule: {},
    rosters: {},
    scoring: {},
    transactions: {},
    dynasty: {},
    draft: {}
  });
  
  // Check if user is commissioner
  const isCommissioner = league?.creatorId === (user?.userId || user?.id || user?.sub);

  if (!isCommissioner) {
    return (
      <div className="card">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <WarningIcon size={24} style={{ color: 'var(--danger)' }} /> Access Denied
        </h2>
        <p>Only the league commissioner can access settings.</p>
        <button className="btn btn-primary" onClick={() => navigate(`/league/${league?.id}/roster`)}>
          Back to League Home
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', Icon: CogIcon },
    { id: 'members', label: 'Members & Teams', Icon: UsersIcon },
    { id: 'schedule', label: 'Schedule & Matchups', Icon: CalendarIcon },
    { id: 'rosters', label: 'Rosters', Icon: ClipboardIcon },
    { id: 'scoring', label: 'Scoring', Icon: TargetIcon },
    { id: 'transactions', label: 'Transactions', Icon: RefreshIcon },
    { id: 'dynasty', label: 'Dynasty', Icon: CrownIcon },
    { id: 'draft', label: 'Draft', Icon: DiceIcon },
  ];

  const updateTabSettings = (tabId, key, value) => {
    setTabChanges(prev => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        [key]: value
      }
    }));
  };

  const hasUnsavedChanges = (tabId) => {
    return Object.keys(tabChanges[tabId] || {}).length > 0;
  };

  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges(activeTab)) {
      setPendingTab(newTab);
      setShowUnsavedWarning(true);
    } else {
      setActiveTab(newTab);
    }
  };

  const confirmTabChange = () => {
    setShowUnsavedWarning(false);
    if (pendingTab) {
      // Discard changes for current tab
      setTabChanges(prev => ({
        ...prev,
        [activeTab]: {}
      }));
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  };

  const cancelTabChange = () => {
    setShowUnsavedWarning(false);
    setPendingTab(null);
  };

  // Save handler for current active tab
  const { saving, error, success, handleSave } = useSaveSettings(league, setLeague, tabChanges[activeTab], () => {
    setTabChanges(prev => ({
      ...prev,
      [activeTab]: {}
    }));
  });

  // Increment when user discards so tab content remounts and UI reverts to saved values
  const [settingsResetKey, setSettingsResetKey] = useState(0);

  const handleDiscard = () => {
    setTabChanges(prev => ({
      ...prev,
      [activeTab]: {}
    }));
    setSettingsResetKey(k => k + 1);
  };

  const tabButton = (tab, isSideNav = false) => {
    const { Icon } = tab;
    const isActive = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        className={
          isSideNav
            ? `league-settings-side-nav-btn${isActive ? ' active' : ''}`
            : `tab-button${isActive ? ' active' : ''}`
        }
        style={{ position: 'relative', whiteSpace: 'nowrap' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {Icon && <Icon size={16} />} {tab.label}
        </span>
        {hasUnsavedChanges(tab.id) && (
          <span style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: 'var(--warning)',
          }} />
        )}
      </button>
    );
  };

  return (
    <div>
      <div className="league-settings-container">
        {/* Side Navigation (desktop) */}
        <aside className="league-settings-side-nav">
          <h2 className="league-settings-label">League Settings</h2>
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            {tabs.map(tab => tabButton(tab, true))}
          </div>
        </aside>

        {/* Top Tab Navigation (mobile/tablet) */}
          <div className="league-settings-top-nav">
            <div className="card" style={{ padding: '0', overflow: 'hidden', boxShadow: 'none' }}>
              <div style={{ display: 'flex', overflow: 'hidden', borderBottom: '1px solid var(--border)' }}>
                {tabs.map(tab => tabButton(tab, false))}
              </div>
            </div>
          </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="apple-overlay">
          <div className="apple-modal" style={{ maxWidth: '480px', width: '90vw' }}>
            <h3 className="text-subheader" style={{ marginBottom: '8px' }}>Unsaved Changes</h3>
            <p className="text-secondary" style={{ marginBottom: '20px' }}>
              You have unsaved changes in the current tab. If you leave, your changes will be lost.
            </p>
            <div className="card-actions">
              <button className="btn btn-secondary" onClick={cancelTabChange}>Stay on Tab</button>
              <button className="btn btn-danger" onClick={confirmTabChange}>Discard Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content - key forces remount on discard so uncontrolled inputs revert to saved values */}
      <div className="card" style={{ marginBottom: '100px' }}>
        {activeTab === 'general' && (
          <GeneralSettings 
            key={`general-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.general}
            updateSettings={(key, value) => updateTabSettings('general', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, general: {} }))}
          />
        )}
        {activeTab === 'members' && (
          <MembersSettings 
            key={`members-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.members}
            updateSettings={(key, value) => updateTabSettings('members', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, members: {} }))}
          />
        )}
        {activeTab === 'schedule' && (
          <ScheduleSettings 
            key={`schedule-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.schedule}
            updateSettings={(key, value) => updateTabSettings('schedule', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, schedule: {} }))}
          />
        )}
        {activeTab === 'rosters' && (
          <RostersSettings 
            key={`rosters-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.rosters}
            updateSettings={(key, value) => updateTabSettings('rosters', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, rosters: {} }))}
          />
        )}
        {activeTab === 'scoring' && (
          <ScoringSettings 
            key={`scoring-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.scoring}
            updateSettings={(key, value) => updateTabSettings('scoring', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, scoring: {} }))}
          />
        )}
        {activeTab === 'transactions' && (
          <TransactionsSettings 
            key={`transactions-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.transactions}
            updateSettings={(key, value) => updateTabSettings('transactions', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, transactions: {} }))}
          />
        )}
        {activeTab === 'dynasty' && (
          <DynastySettings 
            key={`dynasty-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.dynasty}
            updateSettings={(key, value) => updateTabSettings('dynasty', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, dynasty: {} }))}
          />
        )}
        {activeTab === 'draft' && (
          <DraftSettings 
            key={`draft-${settingsResetKey}`}
            league={league} 
            setLeague={setLeague}
            tabChanges={tabChanges.draft}
            updateSettings={(key, value) => updateTabSettings('draft', key, value)}
            clearChanges={() => setTabChanges(prev => ({ ...prev, draft: {} }))}
          />
        )}
      </div>
      </div>

      {/* Floating Action Bar */}
      <FloatingActionBar
        saving={saving}
        error={error}
        success={success}
        hasChanges={hasUnsavedChanges(activeTab)}
        onSave={handleSave}
        onDiscard={handleDiscard}
        activeTab={activeTab}
      />
    </div>
  );
}

// Helper function to convert dot-notation keys to nested objects
function flatToNested(obj) {
  const result = {};
  
  for (const key in obj) {
    const keys = key.split('.');
    let current = result;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = obj[key];
  }
  
  return result;
}

// Reusable Save Hook
function useSaveSettings(league, setLeague, tabChanges, clearChanges) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (Object.keys(tabChanges).length === 0) {
        setSuccess('No changes to save');
        setSaving(false);
        return;
      }
      
      // Convert dot-notation keys to nested objects
      const nestedChanges = flatToNested(tabChanges);
      
      // Valid top-level League fields (excluding read-only/identifier fields)
      const validFields = ['name', 'description', 'draftDate', 'draftTime', 'isPrivate', 'status'];
      
      // Merge nested changes with existing league data for proper structure
      const updates = {};
      let mergedSettings = null;
      let mergedSeason = null;
      
      // Fields that should be nested inside settings
      const settingsFields = ['schedule', 'battingCategories', 'pitchingCategories', 'rosterSettings', 'transactions'];
      
      for (const key in nestedChanges) {
        if (key === 'settings') {
          // Accumulate settings changes
          if (!mergedSettings) {
            mergedSettings = typeof league.settings === 'string' ? JSON.parse(league.settings) : (league.settings || {});
          }
          mergedSettings = { ...mergedSettings, ...nestedChanges.settings };
        } else if (key === 'season') {
          // Accumulate season changes
          if (!mergedSeason) {
            mergedSeason = typeof league.season === 'string' ? JSON.parse(league.season) : (league.season || {});
          }
          mergedSeason = { ...mergedSeason, ...nestedChanges.season };
        } else if (key === 'teams') {
          // Teams should already be properly formatted
          updates.teams = typeof nestedChanges.teams === 'string' ? nestedChanges.teams : JSON.stringify(nestedChanges.teams);
        } else if (settingsFields.includes(key)) {
          // These fields should go into settings, not as top-level fields
          if (!mergedSettings) {
            mergedSettings = typeof league.settings === 'string' ? JSON.parse(league.settings) : (league.settings || {});
          }
          mergedSettings = { ...mergedSettings, [key]: nestedChanges[key] };
        } else if (validFields.includes(key)) {
          // Only include valid top-level fields
          updates[key] = nestedChanges[key];
        } else {
          console.warn(`Skipping invalid field: ${key}`);
        }
      }
      
      // Add merged settings if any
      if (mergedSettings) {
        updates.settings = JSON.stringify(mergedSettings);
      }
      
      // Add merged season if any
      if (mergedSeason) {
        updates.season = JSON.stringify(mergedSeason);
      }
      
      console.log('Saving updates:', updates);
      
      const result = await leagueApiService.updateLeague(league.leagueId || league.id, updates);
      
      if (result.success) {
        setSuccess('✅ Settings saved successfully!');
        
        // Update local league state with nested changes
        if (setLeague) {
          const updatedLeague = { ...league };
          for (const key in updates) {
            if (key === 'settings' || key === 'season') {
              updatedLeague[key] = typeof updates[key] === 'string' ? JSON.parse(updates[key]) : updates[key];
            } else {
              updatedLeague[key] = updates[key];
            }
          }
          setLeague(updatedLeague);
        }
        
        // Clear the changes
        clearChanges();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(tabChanges).length > 0;

  return { saving, error, success, handleSave, hasChanges };
}

// Floating Action Bar Component
function FloatingActionBar({ saving, error, success, hasChanges, onSave, onDiscard, activeTab }) {
  if (!hasChanges) return null;

  const tabLabels = {
    general: 'General Settings',
    members: 'Members & Teams',
    schedule: 'Schedule',
    rosters: 'Roster Settings',
    scoring: 'Scoring',
    transactions: 'Transactions',
    dynasty: 'Dynasty',
    draft: 'Draft'
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'flex-end'
    }}>
      {/* Status Messages */}
      {error && (
        <div className="alert alert-danger" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: '400px', animation: 'slideIn 0.3s ease-out' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: '400px', animation: 'slideIn 0.3s ease-out' }}>
          {success}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        backgroundColor: 'var(--background)',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        padding: '12px 16px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        border: '1px solid var(--border)',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <span style={{ 
          color: 'var(--muted)', 
          fontSize: '13px', 
          marginRight: '8px',
          fontWeight: '500'
        }}>
          Unsaved changes in {tabLabels[activeTab] || 'Settings'}
        </span>
        <button 
          className="btn btn-secondary" 
          onClick={onDiscard}
          disabled={saving}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          Discard
        </button>
        <button 
          className="btn btn-primary" 
          onClick={onSave}
          disabled={saving || !hasChanges}
          style={{ opacity: (saving || !hasChanges) ? 0.6 : 1 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

// General Settings Component
function GeneralSettings({ league, setLeague, tabChanges, updateSettings, clearChanges }) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showScoringPopover, setShowScoringPopover] = useState(false);
  const scoringPopoverRef = useRef(null);

  useEffect(() => {
    if (!showScoringPopover) return;
    const handleClickOutside = (e) => {
      if (scoringPopoverRef.current && !scoringPopoverRef.current.contains(e.target)) {
        setShowScoringPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showScoringPopover]);

  const handleDeleteLeague = async () => {
    setDeleting(true);
    setDeleteError(null);
    
    try {
      const leagueId = league?.leagueId || league?.id;
      if (!leagueId) {
        throw new Error('League ID not found');
      }

      const result = await leagueApiService.deleteLeague(leagueId);
      
      if (result.success) {
        // Navigate to dashboard after successful deletion
        navigate('/');
      } else {
        throw new Error('Failed to delete league');
      }
    } catch (error) {
      console.error('Error deleting league:', error);
      setDeleteError(error.message || 'Failed to delete league. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="form-group">
        <label>League Name</label>
        <input 
          type="text" 
          className="form-control" 
          defaultValue={league?.name}
          onChange={(e) => updateSettings('name', e.target.value)}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        />
      </div>

      <div className="form-group">
        <label>League Description</label>
        <textarea 
          className="form-control" 
          rows="4"
          defaultValue={league?.description}
          onChange={(e) => updateSettings('description', e.target.value)}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        />
      </div>

      <div className="form-group" ref={scoringPopoverRef} style={{ position: 'relative' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          Scoring Type
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              type="button"
              onClick={() => setShowScoringPopover(!showScoringPopover)}
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                padding: 0,
                border: 'none',
                background: 'none',
                color: 'var(--muted)'
              }}
              aria-label="Scoring system descriptions"
            >
              <InfoIcon size={14} />
            </button>
            {showScoringPopover && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '100%',
                  marginLeft: '8px',
                  minWidth: '360px',
                  maxWidth: '480px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  backgroundColor: 'var(--background)',
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  border: '1px solid var(--border)',
                  padding: '16px',
                  zIndex: 1050
                }}
              >
            <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>Scoring System Descriptions</div>
            {Object.entries(SCORING_TYPE_DESCRIPTIONS).map(([name, desc]) => (
              <div key={name} style={{ marginBottom: '14px' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--foreground)', marginBottom: '4px' }}>{name}</div>
                <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
              </div>
            )}
          </span>
        </label>
        <select
          className="form-control"
          defaultValue={league?.settings?.scoringType || 'Rotisserie'}
          onChange={(e) => updateSettings('settings.scoringType', e.target.value)}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        >
          <option value="Rotisserie">Rotisserie</option>
          <option value="Points-based">Points-based</option>
          <option value="Head-to-Head Points">Head-to-Head Points</option>
          <option value="Head-to-Head Most Categories">Head-to-Head Most Categories</option>
          <option value="Head-to-Head Each Category">Head-to-Head Each Category</option>
          <option value="Rotisserie Regular Season">Rotisserie Regular Season</option>
        </select>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
          {SCORING_TYPE_DESCRIPTIONS[league?.settings?.scoringType || tabChanges?.['settings.scoringType']] || SCORING_TYPE_DESCRIPTIONS['Rotisserie']}
        </small>
      </div>

      <div className="form-group">
        <label>League Visibility</label>
        <select 
          className="form-control"
          defaultValue={league?.isPrivate ? 'private' : 'public'}
          onChange={(e) => updateSettings('isPrivate', e.target.value === 'private')}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        >
          <option value="public">Public</option>
          <option value="private">Private (Join by email invite only)</option>
        </select>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
          Public leagues can be discovered; private leagues are hidden. All leagues require an email invite to join.
        </small>
      </div>

      {/* Delete League */}
      <div style={{ marginTop: '40px', paddingTop: '20px' }}>
        {deleteError && (
          <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
            {deleteError}
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={deleting}
          className="btn btn-danger"
          style={{ opacity: deleting ? 0.6 : 1 }}
        >
          {deleting ? 'Deleting...' : 'Delete League'}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'var(--background)',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ fontSize: '20px', color: 'var(--danger)', marginBottom: '15px' }}>
              ⚠️ Delete League?
            </h3>
            <p style={{ marginBottom: '20px', color: 'var(--foreground)', lineHeight: '1.6' }}>
              Are you sure you want to delete <strong>"{league?.name}"</strong>? This action cannot be undone and will permanently delete:
            </p>
            <ul style={{ marginBottom: '20px', paddingLeft: '20px', color: 'var(--muted)' }}>
              <li>All teams and rosters</li>
              <li>All matchups and schedule data</li>
              <li>All league settings and history</li>
            </ul>
            <p style={{ marginBottom: '20px', color: 'var(--danger)', fontWeight: 'bold' }}>
              This action is permanent and cannot be reversed!
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteLeague}
                disabled={deleting}
                style={{ padding: '10px 20px', fontWeight: 'bold' }}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete League'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Invite Code Card Component
function InviteCodeCard({ team, league, updateTeamCode }) {
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Ensure we have a valid league ID
  const leagueId = league?.leagueId || league?.id;
  
  const [generatedCode] = useState(() => {
    // Generate invite code once if it doesn't exist
    if (!team.inviteCode && leagueId) {
      return `${leagueId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
    return null;
  });
  
  const inviteCode = team.inviteCode || generatedCode;
  const inviteLink = inviteCode && leagueId 
    ? `${window.location.origin}/join-invite?code=${inviteCode}&league=${leagueId}`
    : null;
  
  // Show warning if invite code had to be generated
  const isCodeMissing = !team.inviteCode;
  
  // Show error if we couldn't generate a valid link
  const hasError = !inviteLink;
  
  // Update team with generated code if needed
  React.useEffect(() => {
    if (isCodeMissing && generatedCode && updateTeamCode) {
      updateTeamCode(team.id, generatedCode);
    }
  }, [isCodeMissing, generatedCode, team.id, updateTeamCode]);

  const copyToClipboard = () => {
    if (!inviteLink) {
      alert('Cannot copy: Invite link is not available. Please check that the league ID is valid.');
      return;
    }
    
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link');
    });
  };
  
  // Debug logging
  React.useEffect(() => {
    console.log('InviteCodeCard Debug:', {
      teamId: team.id,
      teamName: team.name,
      teamInviteCode: team.inviteCode,
      generatedCode,
      finalInviteCode: inviteCode,
      leagueId,
      inviteLink,
      hasError
    });
  }, [team.id, team.name, team.inviteCode, generatedCode, inviteCode, leagueId, inviteLink, hasError]);

  return (
    <div style={{
      backgroundColor: 'var(--background)',
      padding: '16px',
      borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{
              fontSize: '24px'
            }}>
              {team.logoEmoji || '⚾'}
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {team.name}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                Invited: {team.ownerEmail}
              </div>
            </div>
          </div>
          
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
            Invited: {new Date(team.invitedAt).toLocaleDateString()} at {new Date(team.invitedAt).toLocaleTimeString()}
          </div>
        </div>
        
        <button 
          className="btn btn-primary"
          onClick={copyToClipboard}
          disabled={hasError}
          style={{ 
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 'bold',
            backgroundColor: copySuccess ? 'var(--success)' : 'var(--primary)',
            border: 'none',
            transition: 'all 0.3s ease',
            minWidth: '120px',
            opacity: hasError ? 0.5 : 1,
            cursor: hasError ? 'not-allowed' : 'pointer'
          }}
        >
          {copySuccess ? '✓ Copied!' : hasError ? '❌ Error' : '🔗 Copy Link'}
        </button>
      </div>
      
      {hasError ? (
        <div className="alert alert-danger" style={{ fontSize: '13px' }}>
          <strong>⚠️ Error:</strong> Unable to generate invite link.
          <br />
          League ID: <code>{leagueId || 'undefined'}</code>
          <br />
          Invite Code: <code>{inviteCode || 'undefined'}</code>
          <br />
          <em>Please check the browser console for details.</em>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--surface-muted)',
          padding: '10px',
          borderRadius: '6px',
          border: '1px solid var(--border)',
          fontFamily: 'var(--font-body)',
          fontSize: '12px',
          wordBreak: 'break-all',
          color: 'var(--foreground)'
        }}>
          {inviteLink}
        </div>
      )}
      
      {isCodeMissing ? (
        <div className="alert alert-danger" style={{ marginTop: '10px', fontSize: '12px' }}>
          ⚠️ <strong>Warning:</strong> Invite code was not found. A temporary code has been generated.
          To make it permanent, click "Save Changes" at the top of the page.
        </div>
      ) : (
        <div className="alert alert-warning" style={{ marginTop: '10px', fontSize: '12px' }}>
          💡 <strong>Tip:</strong> Send this link to {team.ownerEmail} so they can join your league
        </div>
      )}
    </div>
  );
}

// Members & Teams Settings Component
function MembersSettings({ league, setLeague, tabChanges, updateSettings, clearChanges }) {
  const [maxTeams, setMaxTeams] = useState(league?.settings?.maxTeams || 12);
  const teamsArray = Array.isArray(league?.teams) ? league.teams : 
                     (typeof league?.teams === 'string' ? JSON.parse(league.teams || '[]') : []);
  const [teams, setTeams] = useState(teamsArray);
  const [editingTeamNames, setEditingTeamNames] = useState({}); // teamId -> teamName
  const [editingAcronyms, setEditingAcronyms] = useState({}); // teamId -> acronym
  const [inviteEmails, setInviteEmails] = useState({}); // teamId -> email
  const [showSecondOwnerRows, setShowSecondOwnerRows] = useState({}); // teamId -> boolean
  const [secondOwnerEmails, setSecondOwnerEmails] = useState({}); // teamId -> email
  const [showDeleteOwnerConfirm, setShowDeleteOwnerConfirm] = useState(false);
  const [teamToDeleteOwner, setTeamToDeleteOwner] = useState(null);
  const [deletingOwner, setDeletingOwner] = useState(false);

  // Helper to get default team name
  const getTeamName = (team, index) => {
    if (team.name && team.name.trim() !== '') {
      return team.name;
    }
    return `Team ${index + 1}`;
  };

  // Helper to check if team has owner
  const hasOwner = (team) => {
    return team.owner && team.owner.trim() !== '' && team.status !== 'invited';
  };

  // Helper to check if team is invited
  const isInvited = (team) => {
    return team.status === 'invited' && team.ownerEmail;
  };

  const handleMaxTeamsChange = (newMax) => {
    const currentCount = teams.length;
    if (newMax < currentCount) {
      const toRemove = teams.slice(newMax);
      const withOwners = toRemove.filter((t) => hasOwner(t) || isInvited(t));
      if (withOwners.length > 0) {
        const names = withOwners.map((t, i) => t.name || `Team ${newMax + i + 1}`).join(', ');
        alert(
          `Cannot reduce the number of teams.\n\n` +
          `${withOwners.length} of the teams that would be removed have owners or pending invites: ${names}\n\n` +
          `Remove the owners from those teams first (use "Delete Owner" or cancel invites), then reduce the number of teams.`
        );
        return;
      }
      const updatedTeams = teams.slice(0, newMax);
      setTeams(updatedTeams);
      updateSettings('teams', updatedTeams);
    } else if (newMax > currentCount) {
      const toAdd = newMax - currentCount;
      const teamEmojis = ['⚾', '🏆', '🔥', '⚡', '🌟', '💪', '🎯', '👑', '🦅', '🐻', '🐯', '🦁', '🏈', '⚽', '🏀', '🎾'];
      const teamColors = ['#007AFF', '#FF3B30', '#34C759', '#FF9500', '#AF52DE', '#FF2D92', '#5AC8FA', '#FFCC00', '#8E8E93', '#5856D6', '#FF9500', '#00C7BE'];
      const newTeams = [];
      for (let i = 0; i < toAdd; i++) {
        const teamNumber = currentCount + i + 1;
        const teamId = `team-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`;
        newTeams.push({
          id: teamId,
          name: `Team ${teamNumber}`,
          acronym: `T${teamNumber}`,
          owner: null,
          ownerName: null,
          ownerEmail: null,
          status: null,
          inviteCode: null,
          logoEmoji: teamEmojis[i % teamEmojis.length],
          logoColor: teamColors[i % teamColors.length],
          points: 0,
          createdAt: new Date().toISOString()
        });
      }
      const updatedTeams = [...teams, ...newTeams];
      setTeams(updatedTeams);
      updateSettings('teams', updatedTeams);
    }
    setMaxTeams(newMax);
    const currentSettings = league?.settings || {};
    updateSettings('settings', { ...currentSettings, maxTeams: newMax });
  };

  const handleTeamNameChange = (teamId, newName) => {
    setEditingTeamNames({ ...editingTeamNames, [teamId]: newName });
  };

  const handleTeamNameBlur = (teamId) => {
    const newName = editingTeamNames[teamId];
    if (newName) {
      const updatedTeams = teams.map(t => 
        t.id === teamId ? { ...t, name: newName } : t
      );
      setTeams(updatedTeams);
      updateSettings('teams', updatedTeams);
      const newEditing = { ...editingTeamNames };
      delete newEditing[teamId];
      setEditingTeamNames(newEditing);
    }
  };

  const handleAcronymChange = (teamId, newAcronym) => {
    setEditingAcronyms({ ...editingAcronyms, [teamId]: newAcronym.substring(0, 4).toUpperCase() });
  };

  const handleAcronymBlur = (teamId) => {
    const newAcronym = editingAcronyms[teamId];
    if (newAcronym) {
      const updatedTeams = teams.map(t => 
        t.id === teamId ? { ...t, acronym: newAcronym } : t
      );
      setTeams(updatedTeams);
      updateSettings('teams', updatedTeams);
      const newEditing = { ...editingAcronyms };
      delete newEditing[teamId];
      setEditingAcronyms(newEditing);
    }
  };

  const handleSendInvite = async (teamId, email, isSecondOwner = false) => {
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    try {
      const inviteCode = `${league.leagueId || league.id}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const teamName = team.name || getTeamName(team, teams.indexOf(team));

      let updatedTeams;
      if (isSecondOwner) {
        const secondOwnerInvite = {
          teamId: teamId,
          email: email,
          inviteCode: inviteCode,
          isSecondOwner: true
        };
        updatedTeams = teams.map(t => 
          t.id === teamId ? { 
            ...t, 
            coOwners: [...(t.coOwners || []), secondOwnerInvite]
          } : t
        );
      } else {
        const updatedTeam = {
          ...team,
          ownerEmail: email,
          status: 'invited',
          inviteCode: inviteCode,
          invitedAt: new Date().toISOString()
        };
        updatedTeams = teams.map(t => t.id === teamId ? updatedTeam : t);
      }

      await leagueApiService.updateLeague(league.leagueId || league.id, { teams: updatedTeams });
      setTeams(updatedTeams);
      setLeague({ ...league, teams: updatedTeams });
      if (isSecondOwner) {
        setSecondOwnerEmails({ ...secondOwnerEmails, [teamId]: '' });
        setShowSecondOwnerRows({ ...showSecondOwnerRows, [teamId]: false });
      } else {
        setInviteEmails({ ...inviteEmails, [teamId]: '' });
      }

      const result = await leagueApiService.sendInvitation({
        leagueId: league.leagueId || league.id,
        leagueName: league.name,
        teamName: teamName,
        invitedEmail: email,
        commissionerName: league.teams?.[0]?.ownerName || 'Commissioner'
      });

      alert(`✅ Invitation sent to ${email}!\n\nInvite link: ${result.inviteLink}`);
      console.log('📧 INVITATION LINK:', result.inviteLink);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(`Failed to send invitation: ${error.message}`);
    }
  };

  const handleResendInvite = async (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team || !team.ownerEmail) return;

    await handleSendInvite(teamId, team.ownerEmail, false);
  };

  const handleDeleteOwnerClick = (teamId) => {
    setTeamToDeleteOwner(teamId);
    setShowDeleteOwnerConfirm(true);
  };

  const handleDeleteOwnerConfirm = async () => {
    if (!teamToDeleteOwner) return;
    setDeletingOwner(true);
    try {
      const updatedTeams = teams.map(t => {
        if (t.id === teamToDeleteOwner) {
          return {
            ...t,
            owner: null,
            ownerName: null,
            ownerEmail: null,
            status: null,
            inviteCode: null
          };
        }
        return t;
      });
      const leagueId = league?.leagueId || league?.id;
      const result = await leagueApiService.updateLeague(leagueId, { teams: updatedTeams });
      if (result?.success !== false) {
        setTeams(updatedTeams);
        setLeague({ ...league, teams: updatedTeams });
        setShowDeleteOwnerConfirm(false);
        setTeamToDeleteOwner(null);
      } else {
        throw new Error(result?.error || 'Failed to update');
      }
    } catch (err) {
      console.error('Error removing owner:', err);
      alert(`Failed to remove owner: ${err.message}`);
    } finally {
      setDeletingOwner(false);
    }
  };

  const handleToggleSecondOwnerRow = (teamId) => {
    setShowSecondOwnerRows({ 
      ...showSecondOwnerRows, 
      [teamId]: !showSecondOwnerRows[teamId] 
    });
  };

  const emptySlots = maxTeams - teams.length;

  // Ensure all teams have names for display
  const teamsWithNames = teams.map((team, index) => ({
    ...team,
    name: editingTeamNames[team.id] || team.name || getTeamName(team, index),
    acronym: editingAcronyms[team.id] || team.acronym || (team.name ? team.name.substring(0, 4).toUpperCase() : `T${index + 1}`)
  }));

  return (
    <div>
      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '10px' }}>Maximum Teams</label>
        <select 
          className="form-control"
          value={maxTeams}
          onChange={(e) => handleMaxTeamsChange(parseInt(e.target.value))}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        >
          <option value={6}>6 Teams</option>
          <option value={8}>8 Teams</option>
          <option value={10}>10 Teams</option>
          <option value={12}>12 Teams</option>
          <option value={14}>14 Teams</option>
          <option value={16}>16 Teams</option>
        </select>
        <div style={{ fontSize: '14px', color: 'var(--muted)', marginTop: '5px' }}>
          Current: {teams.length} / {maxTeams} teams
          {emptySlots > 0 && (
            <span style={{ color: 'var(--apple-orange)', marginLeft: '8px' }}>
              ({emptySlots} empty slot{emptySlots > 1 ? 's' : ''})
            </span>
          )}
        </div>
      </div>

      {/* Teams Table */}
      <div className="card roster-style-table-wrap" style={{ padding: 0 }}>
        <div>
        <table className="apple-table">
          <thead>
            <tr>
              <th>Team Name</th>
              <th>Abbreviation</th>
              <th>Owner</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamsWithNames.map((team, index) => {
              const teamHasOwner = hasOwner(team);
              const teamIsInvited = isInvited(team);
              const teamEmail = inviteEmails[team.id] || '';
              const showSecondOwnerRow = showSecondOwnerRows[team.id];
              const secondOwnerEmail = secondOwnerEmails[team.id] || '';

              return (
                <React.Fragment key={team.id}>
                  {/* Primary Team Row */}
                <tr>
                    {/* Team Name */}
                    <td style={{ padding: '6px' }}>
                      <input
                        type="text"
                        className="form-control"
                        value={editingTeamNames[team.id] !== undefined ? editingTeamNames[team.id] : (team.name || getTeamName(team, index))}
                        onChange={(e) => handleTeamNameChange(team.id, e.target.value)}
                        onBlur={() => handleTeamNameBlur(team.id)}
                        style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                        placeholder={`Team ${index + 1}`}
                      />
                    </td>

                    {/* Abbreviation */}
                    <td style={{ padding: '6px' }}>
                      <input
                        type="text"
                        className="form-control"
                        value={editingAcronyms[team.id] !== undefined ? editingAcronyms[team.id] : (team.acronym || (team.name ? team.name.substring(0, 4).toUpperCase() : `T${index + 1}`))}
                        onChange={(e) => handleAcronymChange(team.id, e.target.value)}
                        onBlur={() => handleAcronymBlur(team.id)}
                        maxLength={4}
                        style={{ width: '60px', padding: '6px', fontSize: '14px', textTransform: 'uppercase' }}
                      />
                    </td>

                    {/* Owner */}
                    <td style={{ padding: '6px' }}>
                      {teamHasOwner ? (
                        <div>
                          <div style={{ fontWeight: '500' }}>{team.ownerName || team.owner}</div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{team.ownerEmail || '—'}</div>
                        </div>
                      ) : teamIsInvited ? (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>{team.ownerEmail}</div>
                          <span style={{ 
                            fontSize: '11px', 
                            padding: '2px 6px', 
                            backgroundColor: 'var(--primary)',
                            color: 'var(--background)',
                            borderRadius: '3px',
                            display: 'inline-block'
                          }}>
                            📧 Invited
                          </span>
                        </div>
                      ) : (
                        <input
                          type="email"
                          className="form-control"
                          value={teamEmail}
                          onChange={(e) => setInviteEmails({ ...inviteEmails, [team.id]: e.target.value })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && teamEmail) {
                              handleSendInvite(team.id, teamEmail, false);
                            }
                          }}
                          placeholder="Enter email to invite owner"
                          style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                        />
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '6px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {!teamHasOwner && !teamIsInvited && (
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              const email = inviteEmails[team.id];
                              if (email) {
                                handleSendInvite(team.id, email, false);
                              } else {
                                alert('Please enter an email address first');
                              }
                            }}
                            disabled={!teamEmail}
                            style={{ 
                              padding: '4px 12px', 
                              fontSize: '12px',
                              opacity: teamEmail ? 1 : 0.5,
                              cursor: teamEmail ? 'pointer' : 'not-allowed'
                            }}
                          >
                            Send Invite
                          </button>
                        )}
                        {teamIsInvited && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleResendInvite(team.id)}
                            style={{ padding: '4px 12px', fontSize: '12px' }}
                          >
                            Resend Invite
                          </button>
                        )}
                        {teamHasOwner && (
                          <>
                            {team.owner !== league?.creatorId && (
                              <button
                                type="button"
                                onClick={() => handleDeleteOwnerClick(team.id)}
                                title="Remove owner"
                                style={{
                                  padding: '4px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: 'var(--danger)',
                                  color: 'var(--background)',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <SubtractIcon size={12} />
                              </button>
                            )}
                            {!showSecondOwnerRows[team.id] && (
                              <button
                                type="button"
                                onClick={() => handleToggleSecondOwnerRow(team.id)}
                                title="Add second owner"
                                style={{
                                  padding: '4px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: 'transparent',
                                  color: 'var(--success)',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <PlusIcon size={12} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Second Owner Row (if toggled) */}
                  {showSecondOwnerRow && teamHasOwner && (
                    <tr style={{ backgroundColor: 'rgba(0, 113, 227, 0.05)' }}>
                      <td style={{ padding: '6px', paddingLeft: '40px', fontSize: '13px', color: 'var(--muted)' }}>
                        ↳ Second Owner
                      </td>
                      <td style={{ padding: '6px' }}></td>
                      <td style={{ padding: '6px' }}>
                        <input
                          type="email"
                          className="form-control"
                          value={secondOwnerEmail}
                          onChange={(e) => setSecondOwnerEmails({ ...secondOwnerEmails, [team.id]: e.target.value })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && secondOwnerEmail) {
                              handleSendInvite(team.id, secondOwnerEmail, true);
                            }
                          }}
                          placeholder="second.owner@example.com"
                          style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                        />
                      </td>
                      <td style={{ padding: '6px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              if (secondOwnerEmail) {
                                handleSendInvite(team.id, secondOwnerEmail, true);
                              } else {
                                alert('Please enter an email address first');
                              }
                            }}
                            disabled={!secondOwnerEmail}
                            style={{ 
                              padding: '4px 12px', 
                              fontSize: '12px',
                              opacity: secondOwnerEmail ? 1 : 0.5,
                              cursor: secondOwnerEmail ? 'pointer' : 'not-allowed'
                            }}
                          >
                            Send Invite
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowSecondOwnerRows({ ...showSecondOwnerRows, [team.id]: false });
                              setSecondOwnerEmails({ ...secondOwnerEmails, [team.id]: '' });
                            }}
                            style={{ padding: '4px 12px', fontSize: '12px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* Empty Slots - Now shown as actual teams but without owners */}
            {Array.from({ length: emptySlots }).map((_, index) => {
              const slotNumber = teams.length + index + 1;
              const slotTeamId = `empty-slot-${slotNumber}`;
              
              return (
                <tr key={slotTeamId} style={{ opacity: 0.7 }}>
                  <td style={{ padding: '6px', fontStyle: 'italic', color: 'var(--muted)' }}>
                    <span style={{ marginRight: '8px' }}>⚪</span>
                    Team Slot {slotNumber} - Available
                  </td>
                  <td style={{ padding: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>---</span>
                  </td>
                  <td style={{ padding: '6px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
                      No owner yet
                    </span>
                  </td>
                  <td style={{ padding: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      Increase maximum teams to add this slot
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {emptySlots === 0 && teams.length > 0 && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: 'rgba(52, 199, 89, 0.1)',
          borderRadius: 'var(--apple-radius-md)',
          border: '1px solid rgba(52, 199, 89, 0.2)',
          color: 'var(--success)'
        }}>
          ✅ League is full! All {maxTeams} team slots are occupied. Increase maximum teams to add more slots.
        </div>
      )}

      {/* Fill Teams with Players Button (commissioner dev tool).
          Skips the draft, sets the league Active, and assigns ~20 players to
          every team - useful for building/demoing roster UI on sample data.
          Guarded by its own confirm dialog + the commissioner-only settings. */}
      {teams.length > 0 && (
        <FillTeamsWithPlayersButton
          league={league}
          teams={teams}
        />
      )}

      {/* Remove Owner Confirmation Modal */}
      {showDeleteOwnerConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'var(--background)',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ fontSize: '20px', color: 'var(--danger)', marginBottom: '15px' }}>
              Remove owner?
            </h3>
            <p style={{ marginBottom: '20px', color: 'var(--foreground)', lineHeight: '1.6' }}>
              Are you sure you want to remove this owner from the team? The team will become available for a new owner.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteOwnerConfirm(false);
                  setTeamToDeleteOwner(null);
                }}
                disabled={deletingOwner}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteOwnerConfirm}
                disabled={deletingOwner}
                style={{ padding: '10px 20px', fontWeight: 'bold' }}
              >
                {deletingOwner ? 'Removing...' : 'Yes, Remove Owner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Fill Teams with Players Button Component (for testing)
function FillTeamsWithPlayersButton({ league, teams }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const handleFillRosters = async () => {
    if (!window.confirm(
      `Skip draft and fill all ${teams.length} teams in "${league.name}" with players?\n\n` +
      `This will:\n` +
      `- Mark the draft as completed (skip draft)\n` +
      `- Update league status to Active\n` +
      `- Assign ~20 players per team for testing\n` +
      `- Replace existing rosters\n\n` +
      `Continue?`
    )) {
      return;
    }

    setLoading(true);
    setStatus('Skipping draft and loading players...');

    try {
      // Import services dynamically
      const playerServiceModule = await import('../../services/playerService.js');
      // Prefer the paged DB loader: getAllPlayers() runs a single capped query
      // (~one page), which isn't enough players to fill every team. Falls back
      // to getAllPlayers if the paged variant isn't available.
      const getAllPlayers =
        playerServiceModule.getAllPlayersFromDB ||
        (playerServiceModule.default && playerServiceModule.default.getAllPlayersFromDB) ||
        playerServiceModule.getAllPlayers ||
        (playerServiceModule.default && playerServiceModule.default.getAllPlayers);
      const { getRosterForDate, saveRosterForDate, createEmptyRoster, getCurrentDate } = await import('../../utils/rosterManager.js');
      const apiGraphQLService = (await import('../../services/apiGraphQL.js')).default;
      const { getDraft, updateDraft } = await import('../../services/draftService.js');
      
      // Step 1: Skip draft - mark as completed if it exists
      setStatus('Skipping draft...');
      try {
        const draft = await getDraft(league.leagueId || league.id);
        if (draft && draft.status !== 'completed') {
          const completedDraft = {
            ...draft,
            status: 'completed',
            completedAt: new Date().toISOString()
          };
          await updateDraft(completedDraft);
          setStatus('Draft marked as completed. Updating league status...');
        }
      } catch (draftError) {
        // Draft might not exist yet, that's okay
        console.log('No draft found or error accessing draft:', draftError);
      }
      
      // Step 2: Update league status from 'Draft' to 'Active'
      try {
        await apiGraphQLService.updateLeague(league.leagueId || league.id, {
          status: 'Active'
        });
        setStatus('League status updated. Fetching players...');
      } catch (statusError) {
        console.warn('Could not update league status:', statusError);
        // Continue anyway
      }

      // Get players
      setStatus('Fetching players from database...');
      
      if (!getAllPlayers) {
        throw new Error('Player service not available. Please ensure players are synced from FantasyData API.');
      }

      const playerResult = await getAllPlayers({
        filters: {
          activeOnly: true,
        },
      });

      if (!playerResult.success || !playerResult.players || playerResult.players.length === 0) {
        throw new Error('No players available. Please sync players from FantasyData API first.');
      }

      const allPlayers = playerResult.players || [];
      const playersPerTeam = Math.min(20, Math.floor(allPlayers.length / Math.max(1, teams.length)));
      
      if (playersPerTeam < 5) {
        throw new Error(`Not enough players available. Need at least ${teams.length * 5} players, but found only ${allPlayers.length}. Please sync players from FantasyData API first.`);
      }

      // Organize players by position
      const playersByPosition = {
        C: [], '1B': [], '2B': [], '3B': [], SS: [], MI: [], CI: [], OF: [], UTIL: [], SP: [], RP: []
      };

      // Categorize players by position
      allPlayers.forEach(player => {
        const pos = player.position;
        if (!pos) {
          playersByPosition.UTIL.push(player);
          return;
        }
        
        if (pos === 'C') {
          playersByPosition.C.push(player);
        } else if (pos === '1B') {
          playersByPosition['1B'].push(player);
          playersByPosition.CI.push(player); // Also add to CI
        } else if (pos === '2B') {
          playersByPosition['2B'].push(player);
          playersByPosition.MI.push(player); // Also add to MI
        } else if (pos === '3B') {
          playersByPosition['3B'].push(player);
          playersByPosition.CI.push(player); // Also add to CI
        } else if (pos === 'SS') {
          playersByPosition.SS.push(player);
          playersByPosition.MI.push(player); // Also add to MI
        } else if (['LF', 'CF', 'RF', 'OF'].includes(pos)) {
          playersByPosition.OF.push(player);
        } else if (pos === 'SP') {
          playersByPosition.SP.push(player);
        } else if (pos === 'RP') {
          playersByPosition.RP.push(player);
        } else if (pos === 'P') {
          // Default pitchers to SP for now
          playersByPosition.SP.push(player);
        } else {
          // Any other position goes to UTIL
          playersByPosition.UTIL.push(player);
        }
      });

      const currentDate = getCurrentDate();
      const usedPlayerIds = new Set();
      let teamsFilled = 0;

      // Assign players to each team
      for (let teamIndex = 0; teamIndex < teams.length; teamIndex++) {
        const team = teams[teamIndex];
        // Use team.owner if exists, otherwise use team.id as userId for testing
        // IMPORTANT: If team has no owner, roster will be saved with team.id as userId
        // To view the roster, you'll need to either:
        // 1. Assign an owner to the team, OR
        // 2. View the roster using the Team Roster page which uses team.id
        const userId = team.owner || team.id || `team-${teamIndex}`;
        const leagueId = league.leagueId || league.id;
        
        if (!team.owner) {
          console.warn(`Team "${team.name}" has no owner. Roster will be saved with userId: ${userId}`);
        }

        setStatus(`Filling ${team.name || `Team ${teamIndex + 1}`} (${teamIndex + 1}/${teams.length})...`);

        // Create roster with proper structure
        let roster = createEmptyRoster();
        roster.players = [];
        roster.bench = [];
        roster.minorLeague = [];
        // Initialize activeLineup with all required positions set to null
        roster.activeLineup = {
          C: null, '1B': null, '2B': null, '3B': null, SS: null,
          MI: null, CI: null,
          OF: [], UTIL: [],
          P: []
        };

        // Target distribution per team - match roster structure
        // Active lineup slots: 1 C, 1 1B, 1 2B, 1 3B, 1 SS, 1 MI, 1 CI, 4 OF, 2 UTIL, 9 P
        // Total: ~20 players per team (fills active lineup + bench for testing)
        const targetDistribution = {
          C: 2,        // 1 in lineup, 1 on bench
          '1B': 1,     // 1 in lineup
          '2B': 1,     // 1 in lineup
          '3B': 1,     // 1 in lineup
          SS: 1,       // 1 in lineup
          MI: 1,       // 1 in lineup
          CI: 1,       // 1 in lineup
          OF: 5,       // 4 in lineup (OF1-4), 1 on bench
          UTIL: 2,     // 2 in lineup (UTIL1-2)
          SP: 3,       // Fill pitcher slots (P1-P9) - combined with RP
          RP: 3        // Combined with SP to fill remaining pitcher slots (total 6 pitchers)
        };

        // Add players by position
        for (const [position, count] of Object.entries(targetDistribution)) {
          let availableForPosition = [];

          if (position === 'MI') {
            availableForPosition = [...playersByPosition['2B'], ...playersByPosition.SS];
          } else if (position === 'CI') {
            availableForPosition = [...playersByPosition['1B'], ...playersByPosition['3B']];
          } else if (position === 'UTIL') {
            availableForPosition = [
              ...playersByPosition['1B'], ...playersByPosition['2B'], 
              ...playersByPosition['3B'], ...playersByPosition.SS, 
              ...playersByPosition.OF
            ];
          } else {
            availableForPosition = playersByPosition[position] || [];
          }

          // Filter out used players
          availableForPosition = availableForPosition.filter(p => {
            const pid = p.id || p.playerId || p.fantasyDataPlayerId?.toString();
            return pid && !usedPlayerIds.has(pid);
          });

          // Shuffle and take needed amount
          const shuffled = [...availableForPosition].sort(() => Math.random() - 0.5);
          const toAdd = Math.min(count, shuffled.length);

          for (let i = 0; i < toAdd; i++) {
            const player = shuffled[i];
            const playerId = player.id || player.playerId || player.fantasyDataPlayerId?.toString();
            
            if (playerId && !usedPlayerIds.has(playerId)) {
              const rosterPlayer = {
                id: playerId,
                playerId: player.playerId || player.id,
                name: player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
                firstName: player.firstName,
                lastName: player.lastName,
                position: player.position || position,
                positions: player.positions || [player.position || position],
                team: player.team || 'FA',
                teamId: player.teamId,
                photoUrl: player.photoUrl,
                stats: {
                  // Format stats to match what the app expects
                  battingAverage: player.seasonStats?.avg || player.seasonStats?.battingAverage || 0,
                  homeRuns: player.seasonStats?.hr || player.seasonStats?.homeRuns || 0,
                  rbi: player.seasonStats?.rbi || 0,
                  stolenBases: player.seasonStats?.sb || player.seasonStats?.stolenBases || 0,
                  runs: player.seasonStats?.runs || 0,
                  wins: player.seasonStats?.w || player.seasonStats?.wins || 0,
                  saves: player.seasonStats?.sv || player.seasonStats?.saves || 0,
                  strikeouts: player.seasonStats?.k || player.seasonStats?.strikeouts || 0,
                  era: player.seasonStats?.era || 0,
                  whip: player.seasonStats?.whip || 0
                }
              };

              roster.players.push(rosterPlayer);
              
              // Add to active lineup if slot is empty (match roster structure)
              // Track which OF/UTIL/P slots are filled
              let addedToLineup = false;

              if (position === 'OF') {
                if (!roster.activeLineup.OF) roster.activeLineup.OF = [];
                if (roster.activeLineup.OF.length < 4) {
                  roster.activeLineup.OF.push(rosterPlayer);
                  roster.activeLineup.OF.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  addedToLineup = true;
                }
              } else if (position === 'UTIL') {
                if (!roster.activeLineup.UTIL) roster.activeLineup.UTIL = [];
                if (roster.activeLineup.UTIL.length < 2) {
                  roster.activeLineup.UTIL.push(rosterPlayer);
                  roster.activeLineup.UTIL.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  addedToLineup = true;
                }
              } else if (position === 'SP' || position === 'RP') {
                // Add to P array (max 9), sorted alphabetically
                if (!roster.activeLineup.P) roster.activeLineup.P = [];
                if (roster.activeLineup.P.length < 9) {
                  roster.activeLineup.P.push(rosterPlayer);
                  roster.activeLineup.P.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  addedToLineup = true;
                }
              } else {
                // Other positions (C, 1B, 2B, 3B, SS, MI, CI) map directly
                if (!roster.activeLineup[position]) {
                  roster.activeLineup[position] = rosterPlayer;
                  addedToLineup = true;
                }
              }
              
              // If not added to lineup, add to bench
              if (!addedToLineup) {
                roster.bench.push(rosterPlayer);
              }

              usedPlayerIds.add(playerId);
            }
          }
        }

        // Fill remaining slots with any available players to reach target
        const remaining = Math.max(0, playersPerTeam - roster.players.length);
        const allAvailable = allPlayers.filter(p => {
          const pid = p.id || p.playerId || p.fantasyDataPlayerId?.toString();
          return pid && !usedPlayerIds.has(pid);
        });

        // Shuffle available players
        const shuffled = [...allAvailable].sort(() => Math.random() - 0.5);

        for (let i = 0; i < remaining && i < shuffled.length; i++) {
          const player = shuffled[i];
          const playerId = player.id || player.playerId || player.fantasyDataPlayerId?.toString();
          
          const rosterPlayer = {
            id: playerId,
            playerId: player.playerId || player.id,
            name: player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
            firstName: player.firstName,
            lastName: player.lastName,
            position: player.position || 'UTIL',
            positions: player.positions || [player.position || 'UTIL'],
            team: player.team || 'FA',
            teamId: player.teamId,
                photoUrl: player.photoUrl,
                stats: {
                  // Format stats to match what the app expects
                  battingAverage: player.seasonStats?.avg || player.seasonStats?.battingAverage || 0,
                  homeRuns: player.seasonStats?.hr || player.seasonStats?.homeRuns || 0,
                  rbi: player.seasonStats?.rbi || 0,
                  stolenBases: player.seasonStats?.sb || player.seasonStats?.stolenBases || 0,
                  runs: player.seasonStats?.runs || 0,
                  wins: player.seasonStats?.w || player.seasonStats?.wins || 0,
                  saves: player.seasonStats?.sv || player.seasonStats?.saves || 0,
                  strikeouts: player.seasonStats?.k || player.seasonStats?.strikeouts || 0,
                  era: player.seasonStats?.era || 0,
                  whip: player.seasonStats?.whip || 0
                }
              };

          roster.players.push(rosterPlayer);
          roster.bench.push(rosterPlayer);
          usedPlayerIds.add(playerId);
        }

        // Ensure roster has minimum structure
        if (!roster.activeLineup) {
          roster.activeLineup = createEmptyRoster().activeLineup;
        }
        if (!roster.bench) {
          roster.bench = [];
        }
        if (!roster.minorLeague) {
          roster.minorLeague = [];
        }
        if (!roster.players) {
          roster.players = [];
        }
        
        // Set lastUpdated timestamp
        roster.lastUpdated = currentDate;
        
        // Validate roster has players before saving
        if (roster.players.length === 0) {
          console.warn(`Warning: No players assigned to team "${team.name}". Skipping this team.`);
          setStatus(`⚠️ Warning: No players available for ${team.name}. Check if players are synced.`);
          // Skip to next team
          continue;
        }
        
        // Debug logging
        console.log(`Saving roster for team "${team.name}" (userId: ${userId}, leagueId: ${leagueId})`);
        console.log(`Roster has ${roster.players.length} players, ${Object.values(roster.activeLineup).filter(p => p !== null).length} in lineup, ${roster.bench.length} on bench`);
        console.log('Sample players:', roster.players.slice(0, 3).map(p => ({ name: p.name, position: p.position })));
        
        try {
          await saveRosterForDate(userId, leagueId, roster, currentDate, league);
          const savedRoster = await getRosterForDate(userId, leagueId, currentDate, league);
          if (savedRoster.players.length !== roster.players.length) {
            console.error(`❌ Error: Roster not saved correctly for team ${team.name}. Expected ${roster.players.length} players, got ${savedRoster.players.length}`);
            throw new Error(`Failed to save roster for ${team.name}`);
          }
          console.log(`✅ Successfully saved roster for team "${team.name}" with ${roster.players.length} players`);
        } catch (saveError) {
          console.error(`Error saving roster for team ${team.name}:`, saveError);
          throw new Error(`Failed to save roster for ${team.name}: ${saveError.message}`);
        }
        
        teamsFilled++;
      }

      // Build summary message
      let summaryMessage = `✅ Successfully skipped draft and filled ${teamsFilled} teams with players!\n\n`;
      summaryMessage += `League status updated to Active.\n\n`;
      summaryMessage += `Note: Rosters are saved for each team's owner. `;
      summaryMessage += `If you don't see players, make sure you're viewing the roster for a team you own, `;
      summaryMessage += `or check the Team Roster page to see all teams' rosters.\n\n`;
      summaryMessage += `Refresh the page to see rosters.`;
      
      setStatus(summaryMessage);
      setTimeout(() => setStatus(''), 10000);
      
      // Log summary to console for debugging
      console.log(`✅ Filled ${teamsFilled} teams with players`);
      console.log('Teams filled:', teams.map((t, i) => ({
        name: t.name || `Team ${i + 1}`,
        owner: t.owner || 'No owner',
        userId: t.owner || t.id || `team-${i}`
      })));

    } catch (error) {
      console.error('Error filling rosters:', error);
      setStatus(`❌ Error: ${error.message}`);
      setTimeout(() => setStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      marginTop: '30px',
      padding: '20px',
      backgroundColor: 'rgba(0, 122, 255, 0.1)',
      border: '1px solid rgba(0, 122, 255, 0.2)',
      borderRadius: 'var(--apple-radius-md)'
    }}>
      <h3 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>
        🧪 Testing Tools
      </h3>
      <p style={{ fontSize: '14px', color: 'var(--apple-text-secondary)', marginBottom: '16px' }}>
        Skip the draft and populate all teams with players for testing. This will mark the draft as completed, update league status to Active, and assign approximately 20 players per team.
      </p>
      
      {status && (
        <div style={{
          padding: '6px',
          marginBottom: '12px',
          backgroundColor: status.includes('✅') ? 'rgba(52, 199, 89, 0.1)' : status.includes('❌') ? 'rgba(255, 59, 48, 0.1)' : 'rgba(0, 122, 255, 0.1)',
          border: `1px solid ${status.includes('✅') ? 'rgba(52, 199, 89, 0.2)' : status.includes('❌') ? 'rgba(255, 59, 48, 0.2)' : 'rgba(0, 122, 255, 0.2)'}`,
          borderRadius: 'var(--apple-radius-sm)',
          fontSize: '14px',
          color: status.includes('✅') ? 'var(--success)' : status.includes('❌') ? 'var(--danger)' : 'var(--primary)'
        }}>
          {status}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary"
        onClick={handleFillRosters}
        disabled={loading}
        style={{
          padding: '12px 24px',
          fontSize: '15px',
          fontWeight: '600'
        }}
      >
        {loading ? (
          <>
            <span className="apple-spinner" style={{ marginRight: '8px' }}></span>
            Filling Rosters...
          </>
        ) : (
          <>
            ✨ Skip Draft & Fill All Teams ({teams.length} teams)
          </>
        )}
      </button>
      
      <small style={{ display: 'block', marginTop: '8px', fontSize: '13px', color: 'var(--apple-text-tertiary)' }}>
        ⚠️ This will skip the draft, mark it as completed, update league status to Active, and replace existing rosters for all teams. Players will be assigned based on positions.
      </small>
    </div>
  );
}

// Schedule Settings Component
function ScheduleSettings({ league, setLeague, tabChanges, updateSettings, clearChanges }) {
  
  // Check if league is rotisserie (no playoffs)
  const scoringType = league?.settings?.scoringType || 'Rotisserie';
  const isRotisserie = scoringType === 'Rotisserie' || scoringType === 'Rotisserie Regular Season';
  
  // Sub-tab state for schedule settings
  const [activeSubTab, setActiveSubTab] = useState('settings');
  
  // State for playoff toggle (for non-rotisserie leagues)
  const [playoffsEnabled, setPlayoffsEnabled] = useState(
    !isRotisserie && (league?.season?.playoffsEnabled !== false)
  );
  
  // Get current playoff start week from tabChanges or league settings
  // tabChanges is already tabChanges.schedule, and keys use dot notation like 'schedule.playoffStartWeek'
  const getCurrentPlayoffStartWeek = () => {
    // Check tabChanges first (unsaved changes)
    // tabChanges is already tabChanges.schedule, so we check for the full key path
    if (tabChanges && tabChanges['schedule.playoffStartWeek'] !== undefined) {
      return tabChanges['schedule.playoffStartWeek'];
    }
    return league?.settings?.schedule?.playoffStartWeek;
  };
  
  const currentPlayoffStartWeek = getCurrentPlayoffStartWeek();
  
  // State for matchups editing
  const teams = Array.isArray(league?.teams) ? league.teams : 
                (typeof league?.teams === 'string' ? JSON.parse(league.teams || '[]') : []);
  const storedMatchups = league?.settings?.schedule?.matchups || [];
  // Calculate total weeks: if playoffs enabled, use playoff start week - 1, otherwise default to 18
  const totalWeeks = playoffsEnabled && currentPlayoffStartWeek ? currentPlayoffStartWeek - 1 : 18;
  const [matchups, setMatchups] = useState(storedMatchups);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const initializedLeagueRef = useRef(null);
  const [draggedTeam, setDraggedTeam] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null); // { matchupId, field: 'team1Id' | 'team2Id' }
  const [dragOverTarget, setDragOverTarget] = useState(null); // { matchupId, field }
  
  // Calculate merged weeks
  const { mergedWeeks, weekMapping } = calculateMergedWeeks(league?.settings || {}, totalWeeks);
  
  // Calculate playoff start period based on end date, teams, and round duration
  const calculatePlayoffStartPeriod = (endDate, startDate, numTeams, roundDuration) => {
    if (!endDate || !startDate || !numTeams || !roundDuration) return null;
    
    // Calculate number of playoff rounds needed
    // For bracket: log2(numTeams) rounds
    const numRounds = Math.ceil(Math.log2(numTeams));
    
    // Total playoff weeks = rounds * roundDuration
    const totalPlayoffWeeks = numRounds * roundDuration;
    
    // Calculate the last week of stats accumulation
    const end = new Date(endDate + 'T00:00:00');
    const start = new Date(startDate + 'T00:00:00');
    
    // Get Monday of start week
    const getMonday = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(d.getFullYear(), d.getMonth(), diff);
    };
    
    const startMonday = getMonday(start);
    const endMonday = getMonday(end);
    
    // Calculate total weeks from start to end
    const totalWeeks = Math.floor((endMonday - startMonday) / (1000 * 60 * 60 * 24 * 7)) + 1;
    
    // Playoff start period = total weeks - playoff weeks + 1
    const playoffStartPeriod = totalWeeks - totalPlayoffWeeks + 1;
    
    return Math.max(1, playoffStartPeriod); // Ensure at least period 1
  };
  
  // Sync playoff start period from calculation when it differs from stored value (avoids marking tab as having pending changes on open)
  useEffect(() => {
    if (playoffsEnabled && league?.season?.startDate && league?.season?.endDate) {
      const numTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
      const roundDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
      const calculatedPeriod = calculatePlayoffStartPeriod(
        league.season.endDate,
        league.season.startDate,
        numTeams,
        roundDuration
      );
      if (calculatedPeriod != null) {
        const current = getCurrentPlayoffStartWeek();
        if (current == null || Number(current) !== calculatedPeriod) {
          updateSettings('schedule.playoffStartWeek', calculatedPeriod);
        }
      }
    }
  }, [playoffsEnabled, league?.season?.startDate, league?.season?.endDate, league?.settings?.schedule?.playoffTeams, league?.settings?.schedule?.playoffMatchupDuration]);

  // Load matchups on mount or when league changes
  useEffect(() => {
    const leagueId = league?.id;
    if (!leagueId) return;
    
    // Reset if league changed
    if (initializedLeagueRef.current !== leagueId) {
      initializedLeagueRef.current = null;
    }
    
    const currentStoredMatchups = league?.settings?.schedule?.matchups || [];
    const currentTeams = Array.isArray(league?.teams) ? league.teams : 
                         (typeof league?.teams === 'string' ? JSON.parse(league.teams || '[]') : []);
    
    // If matchups already exist, just sync state (don't regenerate)
    if (currentStoredMatchups.length > 0) {
      setMatchups(currentStoredMatchups);
      initializedLeagueRef.current = leagueId;
      return;
    }
    
    // Only generate if we don't have matchups and have enough teams, and haven't initialized for this league
    if (currentTeams.length >= 2 && initializedLeagueRef.current !== leagueId) {
      // Calculate total weeks based on playoff settings
      const currentPlayoffsEnabled = !isRotisserie && (league?.season?.playoffsEnabled !== false);
      const currentPlayoffStartWeek = league?.settings?.schedule?.playoffStartWeek;
      const weeksToGenerate = currentPlayoffsEnabled && currentPlayoffStartWeek ? currentPlayoffStartWeek - 1 : totalWeeks;
      
      // Calculate merged weeks for current settings
      const currentMergedWeeks = calculateMergedWeeks(league?.settings || {}, weeksToGenerate);
      // Generate initial schedule if none exists
      const initialMatchups = generateRandomSchedule(
        currentTeams, 
        weeksToGenerate, 
        currentMergedWeeks.mergedWeeks, 
        currentMergedWeeks.weekMapping,
        league?.season?.startDate
      );
      setMatchups(initialMatchups);
      // Don't call updateSettings here - only update when user explicitly generates or modifies
      // This prevents false "unsaved changes" warnings when opening the tab
      initializedLeagueRef.current = leagueId;
    }
  }, [league?.id, league?.settings?.schedule?.matchups?.length, league?.teams?.length, totalWeeks]);
  
  const handlePlayoffsToggle = (enabled) => {
    setPlayoffsEnabled(enabled);
    updateSettings('season.playoffsEnabled', enabled);
    
    // Set playoff start period from calculation when enabling playoffs
    if (enabled) {
      const endDate = league?.season?.endDate;
      const startDate = league?.season?.startDate;
      const numTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
      const roundDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
      const calculatedPeriod = calculatePlayoffStartPeriod(endDate, startDate, numTeams, roundDuration);
      if (calculatedPeriod) {
        updateSettings('schedule.playoffStartWeek', calculatedPeriod);
      }
    }
  };
  
  // Validate that all teams are assigned exactly once per period
  const validateWeekSchedule = (periodMatchups, periodNumber) => {
    const assignedTeamIds = new Set();
    const teamIds = teams.map(t => t.id);
    
    periodMatchups.forEach(m => {
      if (m.team1Id) {
        if (assignedTeamIds.has(m.team1Id)) {
          return { valid: false, error: `Team ${m.team1Id} appears multiple times in Period ${periodNumber}` };
        }
        assignedTeamIds.add(m.team1Id);
      }
      if (m.team2Id) {
        if (assignedTeamIds.has(m.team2Id)) {
          return { valid: false, error: `Team ${m.team2Id} appears multiple times in Period ${periodNumber}` };
        }
        assignedTeamIds.add(m.team2Id);
      }
    });
    
    // Check if all teams are assigned
    const missingTeams = teamIds.filter(id => !assignedTeamIds.has(id));
    if (missingTeams.length > 0) {
      return { 
        valid: false, 
        error: `Missing teams in Period ${periodNumber}: ${missingTeams.length} team(s) not assigned`,
        missing: missingTeams
      };
    }
    
    return { valid: true };
  };
  
  // Handle drag start
  const handleDragStart = (e, teamId, matchupId, field) => {
    if (!teamId) {
      e.preventDefault();
      return;
    }
    setDraggedTeam(teamId);
    setDraggedFrom({ matchupId, field });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', teamId);
  };
  
  // Handle drag over
  const handleDragOver = (e, matchupId, field) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ matchupId, field });
  };
  
  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverTarget(null);
  };
  
  // Handle drop - swap teams
  const handleDrop = (e, targetMatchupId, targetField) => {
    e.preventDefault();
    setDragOverTarget(null);
    
    if (!draggedTeam || !draggedFrom) return;
    
    const targetMatchup = matchups.find(m => m.id === targetMatchupId);
    if (!targetMatchup) return;
    
    const sourceMatchup = matchups.find(m => m.id === draggedFrom.matchupId);
    if (!sourceMatchup) return;
    
    const week = targetMatchup.week;
    
    // Get the team being replaced in the target
    const targetTeamId = targetMatchup[targetField];
    
    // If dropping on the same position, do nothing
    if (draggedFrom.matchupId === targetMatchupId && draggedFrom.field === targetField) {
      setDraggedTeam(null);
      setDraggedFrom(null);
      return;
    }
    
    // Check if target team is already used elsewhere in this week (excluding source)
    if (targetTeamId) {
      const weekMatchups = matchups.filter(m => 
        m.week === week && 
        m.id !== targetMatchupId && 
        m.id !== draggedFrom.matchupId
      );
      const isTargetTeamUsed = weekMatchups.some(m => 
        m.team1Id === targetTeamId || m.team2Id === targetTeamId
      );
      
      if (isTargetTeamUsed) {
        alert(`Cannot swap: The team in this position is already used in another matchup this period.`);
        setDraggedTeam(null);
        setDraggedFrom(null);
        return;
      }
    }
    
    // Perform the swap
    const updatedMatchups = matchups.map(m => {
      if (m.id === targetMatchupId) {
        // Update target matchup
        const newMatchup = { ...m };
        newMatchup[targetField] = draggedTeam;
        return newMatchup;
      } else if (m.id === draggedFrom.matchupId) {
        // Update source matchup - replace with target team (or null if target was empty)
        const newMatchup = { ...m };
        newMatchup[draggedFrom.field] = targetTeamId || null;
        return newMatchup;
      }
      return m;
    });
    
    // Validate the week after swap
    const updatedWeekMatchups = updatedMatchups.filter(m => m.week === week);
    const validation = validateWeekSchedule(updatedWeekMatchups, week);
    
    if (!validation.valid && validation.missing && validation.missing.length > 0) {
      console.warn(`Week ${week} validation: ${validation.error}`);
    }
    
    setMatchups(updatedMatchups);
    updateSettings('schedule.matchups', updatedMatchups);
    setDraggedTeam(null);
    setDraggedFrom(null);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setDraggedTeam(null);
    setDraggedFrom(null);
    setDragOverTarget(null);
  };
  
  // Get team by ID
  const getTeamById = (teamId) => {
    return teams.find(t => t.id === teamId);
  };
  
  // Update a specific matchup's teams with validation
  const handleMatchupChange = (matchupId, field, value) => {
    const matchup = matchups.find(m => m.id === matchupId);
    if (!matchup) return;
    
    const week = matchup.week;
    const weekMatchups = matchups.filter(m => m.week === week && m.id !== matchupId);
    
    // Check if the team is already used in this week
    const isTeamUsed = weekMatchups.some(m => 
      (m.team1Id === value && value !== '') || 
      (m.team2Id === value && value !== '')
    );
    
    if (isTeamUsed && value !== '') {
      alert(`This team is already assigned to a matchup in Period ${week}. Every team must have exactly one matchup or bye per period.`);
      return;
    }
    
    // If setting team1, make sure it's not the same as team2
    if (field === 'team1Id' && value === matchup.team2Id && value !== '') {
      alert('A team cannot play against itself. Please select a different team.');
      return;
    }
    
    // If setting team2, make sure it's not the same as team1
    if (field === 'team2Id' && value === matchup.team1Id && value !== '') {
      alert('A team cannot play against itself. Please select a different team.');
      return;
    }
    
    const updatedMatchups = matchups.map(m => {
      if (m.id === matchupId) {
        return { ...m, [field]: value || null };
      }
      return m;
    });
    
    // Validate the week after update
    const updatedWeekMatchups = updatedMatchups.filter(m => m.week === week);
    const validation = validateWeekSchedule(updatedWeekMatchups, week);
    
    if (!validation.valid && validation.missing && validation.missing.length > 0) {
      // Allow the change but warn if teams are missing
      console.warn(`Week ${week} validation: ${validation.error}`);
    }
    
    setMatchups(updatedMatchups);
    updateSettings('schedule.matchups', updatedMatchups);
  };
  
  // Generate new random schedule
  const handleGenerateSchedule = () => {
    if (teams.length < 2) {
      alert('Need at least 2 teams to generate a schedule');
      return;
    }
    
    // Use calculated totalWeeks which respects playoff start week
    const weeksToGenerate = playoffsEnabled && currentPlayoffStartWeek ? currentPlayoffStartWeek - 1 : totalWeeks;
    const newMatchups = generateRandomSchedule(teams, weeksToGenerate, mergedWeeks, weekMapping, league?.season?.startDate);
    setMatchups(newMatchups);
    updateSettings('schedule.matchups', newMatchups);
    setShowGenerateConfirm(false);
  };
  
  // Group matchups by week
  const matchupsByWeek = {};
  matchups.forEach(m => {
    if (!matchupsByWeek[m.week]) {
      matchupsByWeek[m.week] = [];
    }
    matchupsByWeek[m.week].push(m);
  });
  
  // Filter weeks to only show regular season (up to playoff start week - 1)
  let weeks = Object.keys(matchupsByWeek).sort((a, b) => parseInt(a) - parseInt(b));
  if (playoffsEnabled && currentPlayoffStartWeek) {
    weeks = weeks.filter(w => parseInt(w) < currentPlayoffStartWeek);
  }

  return (
    <div>
      {isRotisserie && (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '10px', 
          borderRadius: '4px', 
          marginBottom: '15px',
          border: '1px solid var(--border)'
        }}>
          <strong>ℹ️ Rotisserie League:</strong> This league type uses season-long standings. Playoffs are not required.
        </div>
      )}
      
      {/* Sub-tabs for Schedule & Matchups (only for Head-to-Head leagues) */}
      {!isRotisserie && (
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          borderBottom: '2px solid var(--border)'
        }}>
          <button
            onClick={() => setActiveSubTab('settings')}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 'bold',
              border: 'none',
              borderBottom: activeSubTab === 'settings' ? '3px solid var(--primary)' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: activeSubTab === 'settings' ? 'var(--primary)' : 'var(--muted)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <CalendarIcon size={14} style={{ marginRight: '4px' }} /> Schedule
          </button>
          <button
            onClick={() => setActiveSubTab('matchups')}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 'bold',
              border: 'none',
              borderBottom: activeSubTab === 'matchups' ? '3px solid var(--primary)' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: activeSubTab === 'matchups' ? 'var(--primary)' : 'var(--muted)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <ClipboardIcon size={14} style={{ marginRight: '4px' }} /> Matchups
          </button>
        </div>
      )}
      
      {/* Settings Sub-tab Content (or all content for Rotisserie) */}
      {(isRotisserie || activeSubTab === 'settings') && (
        <>
      {/* Stat Accumulation Period Section - FIRST SETTING */}
      <div style={{
        backgroundColor: 'var(--surface-muted)',
        padding: '6px',
        borderRadius: '4px',
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <span style={{ fontSize: '24px' }}>📊</span>
          Stat Accumulation Period
        </h3>
        
        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Season Start Date</label>
          <input 
            type="date" 
            className="form-control"
            defaultValue={league?.season?.startDate}
            onChange={(e) => {
              updateSettings('season.startDate', e.target.value);
              // Recalculate playoff start if playoffs enabled
              if (playoffsEnabled && league?.season?.endDate) {
                const numTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
                const roundDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
                const calculatedPeriod = calculatePlayoffStartPeriod(league.season.endDate, e.target.value, numTeams, roundDuration);
                if (calculatedPeriod) {
                  updateSettings('schedule.playoffStartWeek', calculatedPeriod);
                }
              }
            }}
            style={{ width: '100%', padding: '6px', fontSize: '14px' }}
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
            When does stat accumulation begin for your league?
          </small>
        </div>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Season End Date</label>
          <input 
            type="date" 
            className="form-control"
            defaultValue={league?.season?.endDate}
            onChange={(e) => {
              updateSettings('season.endDate', e.target.value);
              // Recalculate playoff start if playoffs enabled
              if (playoffsEnabled && league?.season?.startDate) {
                const numTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
                const roundDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
                const calculatedPeriod = calculatePlayoffStartPeriod(e.target.value, league.season.startDate, numTeams, roundDuration);
                if (calculatedPeriod) {
                  updateSettings('schedule.playoffStartWeek', calculatedPeriod);
                }
              }
            }}
            style={{ width: '100%', padding: '6px', fontSize: '14px' }}
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
            Last day of stat accumulation for the year
          </small>
        </div>
      </div>

      {/* Regular Season Section */}
      <div style={{
        backgroundColor: 'var(--surface-muted)',
        padding: '6px',
        borderRadius: '4px',
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <span style={{ fontSize: '24px' }}>⚾</span>
          Regular Season
        </h3>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Matchup Period</label>
          <select 
            className="form-control"
            defaultValue={league?.settings?.schedule?.matchupPeriod || 'weekly'}
            onChange={(e) => updateSettings('schedule.matchupPeriod', e.target.value)}
            style={{ width: '100%', padding: '6px', fontSize: '14px' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-Weekly</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
            How often do matchup periods change? (Applies to Head-to-Head leagues)
          </small>
        </div>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
            <input 
              type="checkbox" 
              defaultChecked={league?.settings?.schedule?.mergeFirstTwoWeeks !== false}
              onChange={(e) => updateSettings('schedule.mergeFirstTwoWeeks', e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>Merge first two matchup periods</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', marginLeft: '26px', fontSize: '12px' }}>
            When season starts on Thursday, merge periods 1 and 2 into a single matchup period
          </small>
        </div>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
            <input 
              type="checkbox" 
              defaultChecked={league?.settings?.schedule?.mergeAllStarBreak !== false}
              onChange={(e) => updateSettings('schedule.mergeAllStarBreak', e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>Merge all-star break period</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', marginLeft: '26px', fontSize: '12px' }}>
            Merge the All-Star break week (July 13-16, 2026) with the following week into a single matchup period
          </small>
        </div>
      </div>
      
      {/* Playoffs Section (only for non-rotisserie) */}
      {!isRotisserie && (
        <div style={{
          backgroundColor: 'var(--surface-muted)',
          padding: '6px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid var(--border)',
          opacity: playoffsEnabled ? 1 : 0.7
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
              <span style={{ fontSize: '20px' }}>🏆</span>
              Playoffs
            </h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
              <input 
                type="checkbox" 
                checked={playoffsEnabled}
                onChange={(e) => handlePlayoffsToggle(e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Enable Playoffs</span>
            </label>
          </div>
          
          {playoffsEnabled ? (
            <>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Playoff Start Period</label>
                <div style={{ padding: '8px 12px', backgroundColor: 'var(--surface-muted)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}>
                  {currentPlayoffStartWeek ? (
                    <>Regular season ends after Period {currentPlayoffStartWeek - 1}. Playoffs begin in Period {currentPlayoffStartWeek} (calculated from season dates, playoff teams, and matchup duration).</>
                  ) : (
                    <>Set season start/end dates, playoff teams, and matchup duration to calculate when playoffs begin.</>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Number of Playoff Teams</label>
                <select 
                  className="form-control"
                  defaultValue={league?.settings?.schedule?.playoffTeams || '4'}
                  onChange={(e) => {
                    updateSettings('schedule.playoffTeams', e.target.value);
                    // Recalculate playoff start period
                    if (league?.season?.startDate && league?.season?.endDate) {
                      const numTeams = parseInt(e.target.value, 10);
                      const roundDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
                      const calculatedPeriod = calculatePlayoffStartPeriod(
                        league.season.endDate, 
                        league.season.startDate, 
                        numTeams, 
                        roundDuration
                      );
                      if (calculatedPeriod) {
                        updateSettings('schedule.playoffStartWeek', calculatedPeriod);
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                >
                  <option value="2">2 Teams (Top 2)</option>
                  <option value="4">4 Teams (Top 4)</option>
                  <option value="6">6 Teams (Top 6)</option>
                  <option value="8">8 Teams (Top 8)</option>
                </select>
                <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
                  How many teams make the playoffs based on regular season standings?
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Playoff Format</label>
                <select 
                  className="form-control"
                  defaultValue={league?.settings?.schedule?.playoffFormat || 'bracket'}
                  onChange={(e) => updateSettings('schedule.playoffFormat', e.target.value)}
                  style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                >
                  <option value="bracket">Single Elimination Bracket</option>
                  <option value="double">Double Elimination</option>
                  <option value="reseeded">Reseeded Each Round</option>
                </select>
                <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
                  Tournament bracket style for playoffs
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>Playoff Matchup Duration</label>
                <select 
                  className="form-control"
                  defaultValue={league?.settings?.schedule?.playoffMatchupDuration || '1'}
                  onChange={(e) => {
                    updateSettings('schedule.playoffMatchupDuration', parseInt(e.target.value));
                    // Recalculate playoff start period
                    if (league?.season?.startDate && league?.season?.endDate) {
                      const numTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
                      const roundDuration = parseInt(e.target.value, 10);
                      const calculatedPeriod = calculatePlayoffStartPeriod(
                        league.season.endDate, 
                        league.season.startDate, 
                        numTeams, 
                        roundDuration
                      );
                      if (calculatedPeriod) {
                        updateSettings('schedule.playoffStartWeek', calculatedPeriod);
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '6px', fontSize: '14px' }}
                >
                  <option value="1">1 Week</option>
                  <option value="2">2 Weeks</option>
                  <option value="3">3 Weeks</option>
                </select>
                <small style={{ color: 'var(--muted)', display: 'block', marginTop: '3px', fontSize: '12px' }}>
                  How many weeks does each playoff matchup last? (e.g., 2 weeks = best-of-2 matchup)
                </small>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
              <p>Playoffs are disabled. Regular season standings will determine the final rankings.</p>
            </div>
          )}
        </div>
      )}
        </>
      )}
      
      {/* Matchups Editor Sub-tab (only for Head-to-Head leagues) */}
      {!isRotisserie && activeSubTab === 'matchups' && (
        <div style={{
          backgroundColor: 'var(--surface-muted)',
          padding: '6px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
              <span style={{ fontSize: '20px' }}>📋</span>
              Weekly Matchups
            </h3>
            <button
              className="btn btn-primary"
              onClick={() => setShowGenerateConfirm(true)}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🔄 Generate New Schedule
            </button>
          </div>
          
          <p style={{ color: 'var(--muted)', marginBottom: '12px', fontSize: '13px' }}>
            Drag and drop teams to swap them between matchups. Each team must have exactly one matchup or bye per week. Changes are saved automatically when you click "Save Schedule Settings".
          </p>
          
          {teams.length < 2 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
              <p>Need at least 2 teams to create matchups. Add teams in the "Members & Teams" tab.</p>
            </div>
          ) : weeks.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
              <p>No matchups generated yet. Click "Generate New Schedule" to create matchups.</p>
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {weeks.map(week => (
            <div key={week} style={{
              marginBottom: '12px',
              padding: '10px',
              backgroundColor: 'var(--background)',
              borderRadius: '4px',
              border: '1px solid var(--border)'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>
                Period {week}
                {league?.season?.startDate && (
                  <span style={{ fontWeight: 'normal', color: 'var(--muted)', marginLeft: '8px', fontSize: '12px' }}>
                    ({getMatchupDateRange(parseInt(week), league.season.startDate, league?.settings)})
                  </span>
                )}
              </h4>
                  <table className="apple-table" style={{ fontSize: '14px' }}>
                    <tbody>
                      {matchupsByWeek[week].map((matchup, idx) => {
                        const team1 = getTeamById(matchup.team1Id);
                        const team2 = getTeamById(matchup.team2Id);
                        const isDraggingTeam1 = draggedFrom?.matchupId === matchup.id && draggedFrom?.field === 'team1Id';
                        const isDraggingTeam2 = draggedFrom?.matchupId === matchup.id && draggedFrom?.field === 'team2Id';
                        const isDragOverTeam1 = dragOverTarget?.matchupId === matchup.id && dragOverTarget?.field === 'team1Id';
                        const isDragOverTeam2 = dragOverTarget?.matchupId === matchup.id && dragOverTarget?.field === 'team2Id';
                        
                        return (
                          <tr key={matchup.id}>
                            <td 
                              style={{ 
                                padding: '6px',
                                minHeight: '40px',
                                backgroundColor: isDragOverTeam1 ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                                transition: 'background-color 0.2s'
                              }}
                              onDragOver={(e) => handleDragOver(e, matchup.id, 'team1Id')}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, matchup.id, 'team1Id')}
                            >
                              {team1 ? (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, matchup.team1Id, matchup.id, 'team1Id')}
                                  onDragEnd={handleDragEnd}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: team1.logoColor || '#007bff',
                                    color: 'var(--background)',
                                    borderRadius: '6px',
                                    cursor: 'move',
                                    display: 'inline-block',
                                    minWidth: '120px',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    opacity: isDraggingTeam1 ? 0.5 : 1,
                                    transition: 'opacity 0.2s',
                                    userSelect: 'none'
                                  }}
                                >
                                  {team1.logoEmoji && <span style={{ marginRight: '6px' }}>{team1.logoEmoji}</span>}
                                  {team1.name}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: isDragOverTeam1 ? 'rgba(0, 113, 227, 0.08)' : 'var(--surface-muted)',
                                    border: `2px dashed ${isDragOverTeam1 ? 'var(--primary)' : 'var(--border)'}`,
                                    borderRadius: 'var(--radius-sm)',
                                    color: isDragOverTeam1 ? 'var(--primary)' : 'var(--muted)',
                                    textAlign: 'center',
                                    minHeight: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: isDragOverTeam1 ? '500' : 'normal',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {isDragOverTeam1 ? 'Drop here' : 'Drop team here'}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold' }}>
                              vs
                            </td>
                            <td 
                              style={{ 
                                padding: '6px',
                                minHeight: '40px',
                                backgroundColor: isDragOverTeam2 ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                                transition: 'background-color 0.2s'
                              }}
                              onDragOver={(e) => handleDragOver(e, matchup.id, 'team2Id')}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, matchup.id, 'team2Id')}
                            >
                              {team2 ? (
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, matchup.team2Id, matchup.id, 'team2Id')}
                                  onDragEnd={handleDragEnd}
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: team2.logoColor || '#007bff',
                                    color: 'var(--background)',
                                    borderRadius: '6px',
                                    cursor: 'move',
                                    display: 'inline-block',
                                    minWidth: '120px',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                    opacity: isDraggingTeam2 ? 0.5 : 1,
                                    transition: 'opacity 0.2s',
                                    userSelect: 'none'
                                  }}
                                >
                                  {team2.logoEmoji && <span style={{ marginRight: '6px' }}>{team2.logoEmoji}</span>}
                                  {team2.name}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: isDragOverTeam2 ? 'rgba(0, 113, 227, 0.08)' : 'rgba(255, 159, 10, 0.1)',
                                    border: `2px dashed ${isDragOverTeam2 ? 'var(--primary)' : 'var(--warning)'}`,
                                    borderRadius: 'var(--radius-sm)',
                                    color: isDragOverTeam2 ? 'var(--primary)' : 'var(--foreground)',
                                    textAlign: 'center',
                                    minHeight: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {isDragOverTeam2 ? 'Drop here' : 'Bye Week'}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  
                  {/* Show unassigned teams for this week */}
                  {(() => {
                    const weekMatchups = matchupsByWeek[week];
                    const assignedTeamIds = new Set();
                    weekMatchups.forEach(m => {
                      if (m.team1Id) assignedTeamIds.add(m.team1Id);
                      if (m.team2Id) assignedTeamIds.add(m.team2Id);
                    });
                    const unassignedTeams = teams.filter(t => !assignedTeamIds.has(t.id));
                    
                    if (unassignedTeams.length > 0) {
                      return (
                        <div className="alert alert-warning" style={{ marginTop: '10px', fontSize: '12px' }}>
                          <strong>⚠️ Unassigned teams:</strong> {unassignedTeams.map(t => t.name).join(', ')}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ))}
              
              {/* Playoff Periods */}
              {playoffsEnabled && currentPlayoffStartWeek && (() => {
                const playoffTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
                const playoffMatchupDuration = parseInt(league?.settings?.schedule?.playoffMatchupDuration || '1', 10);
                
                // Calculate number of playoff rounds
                const numRounds = Math.ceil(Math.log2(playoffTeams));
                
                // Generate playoff periods
                const playoffPeriods = [];
                let currentPeriod = currentPlayoffStartWeek;
                
                for (let round = 1; round <= numRounds; round++) {
                  const teamsInRound = Math.floor(playoffTeams / Math.pow(2, round - 1));
                  const matchupsInRound = teamsInRound / 2;
                  
                  // Generate matchups for this round
                  const roundMatchups = [];
                  for (let i = 0; i < matchupsInRound; i++) {
                    const seed1 = i + 1;
                    const seed2 = teamsInRound - i;
                    roundMatchups.push({
                      seed1,
                      seed2,
                      round,
                      period: currentPeriod
                    });
                  }
                  
                  // Determine round label based on number of teams and round
                  let periodLabel = '';
                  if (playoffTeams === 4) {
                    periodLabel = round === 1 ? 'Semifinals' : 'Finals';
                  } else if (playoffTeams === 6) {
                    periodLabel = round === 1 ? 'First Round' : round === 2 ? 'Semifinals' : 'Finals';
                  } else if (playoffTeams === 8) {
                    periodLabel = round === 1 ? 'Quarterfinals' : round === 2 ? 'Semifinals' : 'Finals';
                  } else {
                    periodLabel = round === numRounds ? 'Finals' : round === numRounds - 1 ? 'Semifinals' : `Round ${round}`;
                  }
                  
                  playoffPeriods.push({
                    period: currentPeriod,
                    round,
                    matchups: roundMatchups,
                    periodLabel
                  });
                  
                  // Move to next period (each round lasts for playoffMatchupDuration periods)
                  currentPeriod += playoffMatchupDuration;
                }
                
                return playoffPeriods.map(playoffPeriod => {
                  // Calculate date range for this playoff period
                  const periodStartDate = league?.season?.startDate 
                    ? getMatchupDateRange(playoffPeriod.period, league.season.startDate, league?.settings)
                    : '';
                  
                  return (
                    <div key={`playoff-${playoffPeriod.period}`} style={{
                      marginBottom: '12px',
                      padding: '10px',
                      backgroundColor: 'rgba(255, 159, 10, 0.08)',
                      borderRadius: 'var(--radius-sm)',
                      border: '2px solid var(--warning)'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold', color: 'var(--foreground)' }}>
                        🏆 Playoffs - {playoffPeriod.periodLabel} (Period {playoffPeriod.period})
                        {periodStartDate && (
                          <span style={{ fontWeight: 'normal', color: 'var(--muted)', marginLeft: '8px', fontSize: '12px' }}>
                            ({periodStartDate})
                          </span>
                        )}
                      </h4>
                      <table className="apple-table" style={{ fontSize: '14px' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '40%' }}>Seed</th>
                            <th style={{ textAlign: 'center', width: '20%' }}>vs</th>
                            <th style={{ width: '40%' }}>Seed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {playoffPeriod.matchups.map((matchup, idx) => (
                            <tr key={`${playoffPeriod.period}-${idx}`}>
                              <td style={{ padding: '6px' }}>
                                <div style={{
                                  padding: '8px 12px',
                                  backgroundColor: 'var(--warning)',
                                  color: 'var(--foreground)',
                                  borderRadius: '6px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  minWidth: '120px',
                                  display: 'inline-block'
                                }}>
                                  Seed {matchup.seed1}
                                </div>
                              </td>
                              <td style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold', color: 'var(--foreground)' }}>
                                vs
                              </td>
                              <td style={{ padding: '6px' }}>
                                <div style={{
                                  padding: '8px 12px',
                                  backgroundColor: 'var(--warning)',
                                  color: 'var(--foreground)',
                                  borderRadius: '6px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  minWidth: '120px',
                                  display: 'inline-block'
                                }}>
                                  Seed {matchup.seed2}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {playoffMatchupDuration > 1 && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--foreground)', fontStyle: 'italic' }}>
                          This matchup lasts {playoffMatchupDuration} period{playoffMatchupDuration > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}
      
      {/* Generate Schedule Confirmation Modal */}
      {showGenerateConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="card" style={{
            maxWidth: '500px',
            margin: '20px',
            padding: '30px'
          }}>
            <h3 style={{ marginTop: 0 }}>⚠️ Generate New Schedule?</h3>
            <p>
              This will replace all existing matchups with a randomly generated schedule. 
              This action cannot be undone. Are you sure you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowGenerateConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleGenerateSchedule}
              >
                Generate Schedule
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}

// Rosters Settings Component
function RostersSettings({ league, setLeague, tabChanges, updateSettings, clearChanges }) {
  const [rosterSettings, setRosterSettings] = useState({
    maxRosterSize: league?.settings?.rosterSettings?.maxRosterSize || 30,
    ilSpots: league?.settings?.rosterSettings?.ilSpots || 3,
    minorLeagueSpots: league?.settings?.rosterSettings?.minorLeagueSpots || 5,
    rosterPeriod: league?.settings?.rosterSettings?.rosterPeriod || 'daily',
    positions: league?.settings?.rosterSettings?.positions || {
      C: 1,
      '1B': 1,
      '2B': 1,
      '3B': 1,
      SS: 1,
      MI: 1,
      CI: 1,
      OF: 4,
      LF: 0,
      CF: 0,
      RF: 0,
      UTIL: 2,
      SP: 5,
      RP: 3,
      'SP/RP': 0,
      P: 0
    }
  });

  const handlePositionChange = (position, value) => {
    const newValue = parseInt(value) || 0;
    const newPositions = { ...rosterSettings.positions, [position]: newValue };
    const newRosterSettings = { ...rosterSettings, positions: newPositions };
    setRosterSettings(newRosterSettings);
    
    // Update parent settings
    const currentSettings = league?.settings || {};
    updateSettings('settings', { ...currentSettings, rosterSettings: newRosterSettings });
  };

  const handleSettingChange = (key, value) => {
    const newValue = parseInt(value) || 0;
    const newRosterSettings = { ...rosterSettings, [key]: newValue };
    setRosterSettings(newRosterSettings);
    
    // Update parent settings
    const currentSettings = league?.settings || {};
    updateSettings('settings', { ...currentSettings, rosterSettings: newRosterSettings });
  };

  const positionDetails = [
    { key: 'C', label: 'Catcher', type: 'Hitters' },
    { key: '1B', label: 'First Base', type: 'Hitters' },
    { key: '2B', label: 'Second Base', type: 'Hitters' },
    { key: '3B', label: 'Third Base', type: 'Hitters' },
    { key: 'SS', label: 'Shortstop', type: 'Hitters' },
    { key: 'MI', label: 'Middle Infielder', type: 'Hitters', description: '2B or SS' },
    { key: 'CI', label: 'Corner Infielder', type: 'Hitters', description: '1B or 3B' },
    { key: 'OF', label: 'Outfield', type: 'Hitters' },
    { key: 'LF', label: 'Left Field', type: 'Hitters' },
    { key: 'CF', label: 'Center Field', type: 'Hitters' },
    { key: 'RF', label: 'Right Field', type: 'Hitters' },
    { key: 'UTIL', label: 'Utility', type: 'Hitters', description: 'Any hitter' },
    { key: 'SP', label: 'Starting Pitcher', type: 'Pitchers' },
    { key: 'RP', label: 'Relief Pitcher', type: 'Pitchers' },
    { key: 'SP/RP', label: 'SP/RP (Flex)', type: 'Pitchers', description: 'SP or RP eligible' },
    { key: 'P', label: 'Pitcher', type: 'Pitchers', description: 'Any pitcher' }
  ];

  const calculateTotalActiveSlots = () => {
    return Object.values(rosterSettings.positions).reduce((sum, val) => sum + val, 0);
  };

  const totalActiveSlots = calculateTotalActiveSlots();
  // Bench slots calculation: IL and Minors don't count against max roster size
  // Max roster size is only for Active Lineup + Bench, IL and Minors are extra
  const benchSlots = rosterSettings.maxRosterSize - totalActiveSlots;
  // Total roster size (active + bench only, IL and minors are separate)
  const totalRosterSize = totalActiveSlots + benchSlots;

  return (
    <div>
      <p style={{ color: 'var(--muted)', marginBottom: '15px', fontSize: '14px' }}>
        Configure the roster structure for all teams in your league
      </p>

      {/* Overall Roster Limits */}
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Overall Roster Limits</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Max Roster Size
            </label>
            <input 
              type="number" 
              className="form-control"
              value={rosterSettings.maxRosterSize}
              onChange={(e) => handleSettingChange('maxRosterSize', e.target.value)}
              min="15"
              max="50"
              style={{ padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Active lineup + bench spots only (IL and minors are extra)
            </small>
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Injured List (IL) Spots
            </label>
            <input 
              type="number" 
              className="form-control"
              value={rosterSettings.ilSpots}
              onChange={(e) => handleSettingChange('ilSpots', e.target.value)}
              min="0"
              max="10"
              style={{ padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              For injured players
            </small>
          </div>

          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Minor League Spots
            </label>
            <input 
              type="number" 
              className="form-control"
              value={rosterSettings.minorLeagueSpots}
              onChange={(e) => handleSettingChange('minorLeagueSpots', e.target.value)}
              min="0"
              max="15"
              style={{ padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              For minor league players
            </small>
          </div>
        </div>
        
        {/* Roster Period Setting */}
        <div className="form-group" style={{ marginTop: '20px' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Roster Period
          </label>
          <select 
            className="form-control"
            value={rosterSettings.rosterPeriod}
            onChange={(e) => handleSettingChange('rosterPeriod', e.target.value)}
            style={{ padding: '10px', fontSize: '16px', maxWidth: '400px' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="weekly-daily-lineup">Weekly w/Daily lineup moves</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            <strong>Daily:</strong> Full roster changes allowed every day. No weekly locks.<br />
            <strong>Weekly:</strong> Rosters can only be set weekly (Mon-Sun). Roster locks when the first game of the week starts.<br />
            <strong>Weekly w/Daily lineup moves:</strong> Weekly roster adds/drops lock when first game starts, but you can still adjust your active lineup daily.
          </small>
        </div>

        {/* Roster Breakdown Summary */}
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: benchSlots < 0 ? 'rgba(255, 59, 48, 0.08)' : 'rgba(52, 199, 89, 0.08)',
          border: `1px solid ${benchSlots < 0 ? 'var(--danger)' : 'var(--success)'}`,
          borderRadius: 'var(--radius-sm)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
            📊 Roster Breakdown:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '13px' }}>
            <div>Active Lineup: <strong>{totalActiveSlots}</strong></div>
            <div>Bench: <strong style={{ color: benchSlots < 0 ? 'var(--danger)' : 'inherit' }}>{benchSlots}</strong></div>
            <div>IL Spots: <strong>{rosterSettings.ilSpots}</strong></div>
            <div>Minor League: <strong>{rosterSettings.minorLeagueSpots}</strong></div>
            <div style={{ fontWeight: 'bold', borderTop: '1px solid var(--border)', paddingTop: '5px' }}>
              Total Roster Size: <strong>{totalRosterSize}</strong> / {rosterSettings.maxRosterSize}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic', marginTop: '5px' }}>
              Plus {rosterSettings.ilSpots} IL spots and {rosterSettings.minorLeagueSpots} minor league spots (not counted against max)
            </div>
          </div>
          {benchSlots < 0 && (
            <div style={{ marginTop: '10px', color: 'var(--danger)', fontSize: '13px' }}>
              ⚠️ Warning: Total positions exceed max roster size! Reduce positions or increase max roster size.
            </div>
          )}
        </div>
      </div>

      {/* Active Lineup Positions Table */}
      <h3 style={{ marginBottom: '15px' }}>Active Lineup Positions</h3>
      
      <div className="roster-style-table-wrap">
        <div>
        <table className="apple-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Full Name</th>
              <th>Description</th>
              <th style={{ textAlign: 'center', width: '150px' }}># of Slots</th>
            </tr>
          </thead>
          <tbody>
            {/* Hitters Section */}
            <tr style={{ backgroundColor: 'rgba(0, 113, 227, 0.06)' }}>
              <td colSpan="4" style={{ padding: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                ⚾ HITTERS
              </td>
            </tr>
            {positionDetails.filter(p => p.type === 'Hitters').map((pos, index) => (
              <tr key={pos.key}>
                <td style={{ padding: '6px', fontWeight: 'bold', fontSize: '16px' }}>
                  {pos.key}
                </td>
                <td style={{ padding: '6px' }}>
                  {pos.label}
                </td>
                <td style={{ padding: '6px', color: 'var(--muted)', fontSize: '14px' }}>
                  {pos.description || '—'}
                </td>
                <td style={{ padding: '6px', textAlign: 'center' }}>
                  <input 
                    type="number" 
                    className="form-control"
                    value={rosterSettings.positions[pos.key]}
                    onChange={(e) => handlePositionChange(pos.key, e.target.value)}
                    min="0"
                    max="10"
                    style={{ 
                      width: '80px', 
                      margin: '0 auto',
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  />
                </td>
              </tr>
            ))}
            
            {/* Pitchers Section */}
            <tr style={{ backgroundColor: 'rgba(52, 199, 89, 0.08)' }}>
              <td colSpan="4" style={{ padding: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                🎯 PITCHERS
              </td>
            </tr>
            {positionDetails.filter(p => p.type === 'Pitchers').map((pos, index) => (
              <tr key={pos.key}>
                <td style={{ padding: '6px', fontWeight: 'bold', fontSize: '16px' }}>
                  {pos.key}
                </td>
                <td style={{ padding: '6px' }}>
                  {pos.label}
                </td>
                <td style={{ padding: '6px', color: 'var(--muted)', fontSize: '14px' }}>
                  {pos.description || '—'}
                </td>
                <td style={{ padding: '6px', textAlign: 'center' }}>
                  <input 
                    type="number" 
                    className="form-control"
                    value={rosterSettings.positions[pos.key]}
                    onChange={(e) => handlePositionChange(pos.key, e.target.value)}
                    min="0"
                    max="15"
                    style={{ 
                      width: '80px', 
                      margin: '0 auto',
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="alert alert-warning" style={{ marginTop: '20px' }}>
        💡 <strong>Tip:</strong> These position settings will apply to all teams in the league. Total roster size (active + bench) should not exceed the max roster size. IL and minor league spots are extra spots that don't count against the max roster size.
      </div>

    </div>
  );
}

// Scoring Settings Component
function ScoringSettings({ league, setLeague, tabChanges, updateSettings, clearChanges }) {
  // Get scoring type from league settings
  const scoringType = league?.settings?.scoringType || 'Rotisserie';
  const isPointsBased = scoringType === 'Points-based' || scoringType === 'Head-to-Head Points';
  const isRotisserie = scoringType === 'Rotisserie' || scoringType === 'Rotisserie Regular Season';
  
  // Available stat categories
  const allBattingStats = [
    { key: 'R', label: 'Runs', isAverage: false },
    { key: 'HR', label: 'Home Runs', isAverage: false },
    { key: 'RBI', label: 'RBI', isAverage: false },
    { key: 'SB', label: 'Stolen Bases', isAverage: false },
    { key: 'H', label: 'Hits', isAverage: false },
    { key: '2B', label: 'Doubles', isAverage: false },
    { key: '3B', label: 'Triples', isAverage: false },
    { key: 'BB', label: 'Walks', isAverage: false },
    { key: 'AVG', label: 'Batting Average', isAverage: true },
    { key: 'OBP', label: 'On-Base Percentage', isAverage: true },
    { key: 'SLG', label: 'Slugging', isAverage: true },
    { key: 'OPS', label: 'OPS', isAverage: true },
    { key: 'TB', label: 'Total Bases', isAverage: false },
    { key: 'CS', label: 'Caught Stealing', isAverage: false },
  ];
  
  const allPitchingStats = [
    { key: 'W', label: 'Wins', isAverage: false },
    { key: 'SV', label: 'Saves', isAverage: false },
    { key: 'K', label: 'Strikeouts', isAverage: false },
    { key: 'QS', label: 'Quality Starts', isAverage: false },
    { key: 'HLD', label: 'Holds', isAverage: false },
    { key: 'IP', label: 'Innings Pitched', isAverage: false },
    { key: 'CG', label: 'Complete Games', isAverage: false },
    { key: 'SHO', label: 'Shutouts', isAverage: false },
    { key: 'L', label: 'Losses', isAverage: false },
    { key: 'BS', label: 'Blown Saves', isAverage: false },
    { key: 'ERA', label: 'ERA', isAverage: true },
    { key: 'WHIP', label: 'WHIP', isAverage: true },
    { key: 'K/9', label: 'K/9', isAverage: true },
    { key: 'BB/9', label: 'BB/9', isAverage: true },
  ];
  
  // Filter out average stats for points leagues
  const availableBattingStats = isPointsBased 
    ? allBattingStats.filter(s => !s.isAverage)
    : allBattingStats;
    
  const availablePitchingStats = isPointsBased 
    ? allPitchingStats.filter(s => !s.isAverage)
    : allPitchingStats;
  
  // Default categories based on scoring type
  const getDefaultCategories = () => {
    const defaultBatting = isPointsBased
      ? [
          { key: 'R', label: 'Runs', points: 1 },
          { key: 'HR', label: 'Home Runs', points: 4 },
          { key: 'RBI', label: 'RBI', points: 1 },
          { key: 'SB', label: 'Stolen Bases', points: 2 },
          { key: 'H', label: 'Hits', points: 1 },
          { key: 'BB', label: 'Walks', points: 1 },
        ]
      : [
          { key: 'R', label: 'Runs' },
          { key: 'HR', label: 'Home Runs' },
          { key: 'RBI', label: 'RBI' },
          { key: 'SB', label: 'Stolen Bases' },
          { key: 'AVG', label: 'Batting Average' },
        ];
        
    const defaultPitching = isPointsBased
      ? [
          { key: 'IP', label: 'Innings Pitched', points: 3 },
          { key: 'W', label: 'Wins', points: 5 },
          { key: 'K', label: 'Strikeouts', points: 1 },
          { key: 'SV', label: 'Saves', points: 5 },
          { key: 'QS', label: 'Quality Starts', points: 3 },
        ]
      : [
          { key: 'W', label: 'Wins' },
          { key: 'SV', label: 'Saves' },
          { key: 'K', label: 'Strikeouts' },
          { key: 'ERA', label: 'ERA' },
          { key: 'WHIP', label: 'WHIP' },
        ];
        
    return { batting: defaultBatting, pitching: defaultPitching };
  };
  
  // Initialize categories from league settings or defaults
  const existingBatting = league?.settings?.battingCategories || getDefaultCategories().batting;
  const existingPitching = league?.settings?.pitchingCategories || getDefaultCategories().pitching;
  
  const [battingCategories, setBattingCategories] = useState(Array.isArray(existingBatting) ? existingBatting : []);
  const [pitchingCategories, setPitchingCategories] = useState(Array.isArray(existingPitching) ? existingPitching : []);
  const [showAddBatting, setShowAddBatting] = useState(false);
  const [showAddPitching, setShowAddPitching] = useState(false);
  
  // Matchup limits state
  const [gamesStartedEnabled, setGamesStartedEnabled] = useState(
    league?.settings?.matchupLimits?.gamesStartedEnabled || false
  );

  // Update settings when categories change
  const handleBattingChange = (newCategories) => {
    setBattingCategories(newCategories);
    updateSettings('battingCategories', newCategories);
  };
  
  const handlePitchingChange = (newCategories) => {
    setPitchingCategories(newCategories);
    updateSettings('pitchingCategories', newCategories);
  };
  
  const handleAddBattingStat = (statKey) => {
    const stat = availableBattingStats.find(s => s.key === statKey);
    if (!stat) return;
    
    const newCat = isPointsBased 
      ? { key: stat.key, label: stat.label, points: 1 }
      : { key: stat.key, label: stat.label };
      
    handleBattingChange([...battingCategories, newCat]);
    setShowAddBatting(false);
  };
  
  const handleAddPitchingStat = (statKey) => {
    const stat = availablePitchingStats.find(s => s.key === statKey);
    if (!stat) return;
    
    const newCat = isPointsBased 
      ? { key: stat.key, label: stat.label, points: 1 }
      : { key: stat.key, label: stat.label };
      
    handlePitchingChange([...pitchingCategories, newCat]);
    setShowAddPitching(false);
  };
  
  const handleRemoveBatting = (index) => {
    handleBattingChange(battingCategories.filter((_, i) => i !== index));
  };
  
  const handleRemovePitching = (index) => {
    handlePitchingChange(pitchingCategories.filter((_, i) => i !== index));
  };
  
  const handlePointsChange = (type, index, value) => {
    const points = parseFloat(value) || 0;
    if (type === 'batting') {
      const updated = [...battingCategories];
      updated[index].points = points;
      handleBattingChange(updated);
    } else {
      const updated = [...pitchingCategories];
      updated[index].points = points;
      handlePitchingChange(updated);
    }
  };

  return (
    <div>
      <p style={{ color: 'var(--muted)', marginBottom: '20px' }}>
        Configure scoring categories for your league. Current scoring type: <strong>{scoringType}</strong>
        {isPointsBased && <span style={{ color: 'var(--primary)' }}> (Points-based)</span>}
      </p>
      
      {isPointsBased && (
        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
          <strong>ℹ️ Points-Based League:</strong> Average-based stats (AVG, OBP, SLG, ERA, WHIP, etc.) are not available. Assign point values to count-based stats.
        </div>
      )}

      {/* Batting Categories Table */}
      <h3 style={{ marginBottom: '15px' }}>⚾ Batting Categories</h3>
      <div className="roster-style-table-wrap" style={{ marginBottom: '30px' }}>
        <div>
        <table className="apple-table">
          <thead>
            <tr>
              <th>Stat</th>
              <th>Category</th>
              {isPointsBased && (
                <th style={{ textAlign: 'center', width: '150px' }}>Points</th>
              )}
              <th style={{ textAlign: 'center', width: '100px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {battingCategories.length === 0 ? (
              <tr>
                <td colSpan={isPointsBased ? 4 : 3} style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
                  No batting categories added. Click "Add Stat Category" to get started.
                </td>
              </tr>
            ) : (
              battingCategories.map((cat, index) => (
                <tr key={index}>
                  <td style={{ padding: '6px', fontWeight: 'bold', fontSize: '16px' }}>
                    {cat.key}
                  </td>
                  <td style={{ padding: '6px' }}>
                    {cat.label}
                  </td>
                  {isPointsBased && (
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <input 
                        type="number" 
                        className="form-control"
                        value={cat.points || 0}
                        onChange={(e) => handlePointsChange('batting', index, e.target.value)}
                        step="0.5"
                        style={{ 
                          width: '100px', 
                          margin: '0 auto',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '14px'
                        }}
                      />
                    </td>
                  )}
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleRemoveBatting(index)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      🗑️ Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
      
      {!showAddBatting ? (
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddBatting(true)}
          style={{ marginBottom: '30px' }}
        >
          ➕ Add Batting Category
        </button>
      ) : (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '15px', 
          borderRadius: '8px',
          marginBottom: '30px'
        }}>
          <h4>Add Batting Category</h4>
          <select 
            className="form-control"
            onChange={(e) => handleAddBattingStat(e.target.value)}
            style={{ marginBottom: '10px' }}
          >
            <option value="">Select a stat...</option>
            {availableBattingStats
              .filter(s => !battingCategories.some(c => c.key === s.key))
              .map(stat => (
                <option key={stat.key} value={stat.key}>
                  {stat.key} - {stat.label}
                </option>
              ))}
          </select>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowAddBatting(false)}
            style={{ fontSize: '14px' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Pitching Categories Table */}
      <h3 style={{ marginBottom: '15px' }}>🎯 Pitching Categories</h3>
      <div className="roster-style-table-wrap" style={{ marginBottom: '30px' }}>
        <div>
        <table className="apple-table">
          <thead>
            <tr>
              <th>Stat</th>
              <th>Category</th>
              {isPointsBased && (
                <th style={{ textAlign: 'center', width: '150px' }}>Points</th>
              )}
              <th style={{ textAlign: 'center', width: '100px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {pitchingCategories.length === 0 ? (
              <tr>
                <td colSpan={isPointsBased ? 4 : 3} style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
                  No pitching categories added. Click "Add Stat Category" to get started.
                </td>
              </tr>
            ) : (
              pitchingCategories.map((cat, index) => (
                <tr key={index}>
                  <td style={{ padding: '6px', fontWeight: 'bold', fontSize: '16px' }}>
                    {cat.key}
                  </td>
                  <td style={{ padding: '6px' }}>
                    {cat.label}
                  </td>
                  {isPointsBased && (
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <input 
                        type="number" 
                        className="form-control"
                        value={cat.points || 0}
                        onChange={(e) => handlePointsChange('pitching', index, e.target.value)}
                        step="0.5"
                        style={{ 
                          width: '100px', 
                          margin: '0 auto',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '14px'
                        }}
                      />
                    </td>
                  )}
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleRemovePitching(index)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      🗑️ Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
      
      {!showAddPitching ? (
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddPitching(true)}
          style={{ marginBottom: '30px' }}
        >
          ➕ Add Pitching Category
        </button>
      ) : (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '15px', 
          borderRadius: '8px',
          marginBottom: '30px'
        }}>
          <h4>Add Pitching Category</h4>
          <select 
            className="form-control"
            onChange={(e) => handleAddPitchingStat(e.target.value)}
            style={{ marginBottom: '10px' }}
          >
            <option value="">Select a stat...</option>
            {availablePitchingStats
              .filter(s => !pitchingCategories.some(c => c.key === s.key))
              .map(stat => (
                <option key={stat.key} value={stat.key}>
                  {stat.key} - {stat.label}
                </option>
              ))}
          </select>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowAddPitching(false)}
            style={{ fontSize: '14px' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Advanced Scoring Options */}
      {!isPointsBased && (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '6px', 
          borderRadius: '4px', 
          marginTop: '40px',
          marginBottom: '15px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ marginBottom: '15px' }}>⚖️ Rotisserie Category Weights</h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>
            Assign weights to scoring categories in rotisserie leagues. Categories with higher weights are worth more points in the standings.
          </p>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                defaultChecked={league?.settings?.scoringSettings?.useCategoryWeights || false}
                onChange={(e) => {
                  const currentSettings = league?.settings || {};
                  const scoringSettings = currentSettings.scoringSettings || {};
                  updateSettings('settings', {
                    ...currentSettings,
                    scoringSettings: {
                      ...scoringSettings,
                      useCategoryWeights: e.target.checked
                    }
                  });
                }}
              />
              <span style={{ fontWeight: 'bold' }}>Use Category Weights</span>
            </label>
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
              Assign different point values to each category (default: all categories worth 1 point)
            </small>
          </div>

          {league?.settings?.scoringSettings?.useCategoryWeights && (
            <div style={{ marginTop: '15px', marginLeft: '30px' }}>
              <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '10px' }}>
                Configure category weights (default: 1.0 for all categories)
              </p>
              <small style={{ color: 'var(--foreground)', fontSize: '12px' }}>
                💡 Category weights will be configurable in a future update. Currently all categories are weighted equally.
              </small>
            </div>
          )}
        </div>
      )}

      {/* Matchup Limits Section */}
      <h3 style={{ marginTop: '40px', marginBottom: '15px' }}>🎯 Matchup Limits</h3>
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>
          Set limits on games started by pitchers and minimum innings pitched for each matchup. This helps prevent teams from streaming too many pitchers.
        </p>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="checkbox" 
              checked={gamesStartedEnabled}
              onChange={(e) => {
                const checked = e.target.checked;
                setGamesStartedEnabled(checked);
                const currentSettings = league?.settings || {};
                const matchupLimits = currentSettings.matchupLimits || {};
                updateSettings('settings', {
                  ...currentSettings,
                  matchupLimits: {
                    ...matchupLimits,
                    gamesStartedEnabled: checked
                  }
                });
              }}
            />
            <span>Enable Games Started (GS) Limit</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Restrict the number of games started by pitchers in each matchup (prevents pitcher streaming)
          </small>
        </div>

        {gamesStartedEnabled && (
          <div className="form-group" style={{ marginTop: '15px', marginLeft: '30px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Maximum Games Started Per Matchup
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.matchupLimits?.maxGamesStarted || 12}
              min={0}
              max={50}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                const currentSettings = league?.settings || {};
                const matchupLimits = currentSettings.matchupLimits || {};
                updateSettings('settings', {
                  ...currentSettings,
                  matchupLimits: {
                    ...matchupLimits,
                    maxGamesStarted: value,
                    gamesStartedEnabled: true
                  }
                });
              }}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Maximum number of pitcher games started allowed per team per matchup (typical range: 10-15)
            </small>
          </div>
        )}

        {/* Minimum IP Requirement */}
        <div className="form-group" style={{ marginTop: '20px' }}>
          <label style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="checkbox" 
              defaultChecked={league?.settings?.matchupLimits?.minimumIPEnabled || false}
              onChange={(e) => {
                const checked = e.target.checked;
                const currentSettings = league?.settings || {};
                const matchupLimits = currentSettings.matchupLimits || {};
                updateSettings('settings', {
                  ...currentSettings,
                  matchupLimits: {
                    ...matchupLimits,
                    minimumIPEnabled: checked
                  }
                });
              }}
            />
            <span>Enable Minimum Innings Pitched (IP) Requirement</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Require teams to pitch a minimum number of innings to qualify for pitching categories in rotisserie/head-to-head leagues
          </small>
        </div>

        {league?.settings?.matchupLimits?.minimumIPEnabled && (
          <div className="form-group" style={{ marginTop: '15px', marginLeft: '30px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Minimum IP Required Per Matchup/Period
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.matchupLimits?.minimumIP || 20}
              min={0}
              max={100}
              step="0.1"
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                const currentSettings = league?.settings || {};
                const matchupLimits = currentSettings.matchupLimits || {};
                updateSettings('settings', {
                  ...currentSettings,
                  matchupLimits: {
                    ...matchupLimits,
                    minimumIP: value,
                    minimumIPEnabled: true
                  }
                });
              }}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Minimum innings pitched required to qualify for pitching categories (typical range: 20-35 IP per week)
            </small>
          </div>
        )}

        {/* Season Innings Cap (settings-layout.html: "Innings / Games Cap") */}
        <div className="form-group" style={{ marginTop: '20px' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Innings / Games Cap
          </label>
          <select
            className="form-control"
            defaultValue={league?.settings?.matchupLimits?.seasonInningsCap ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              const currentSettings = league?.settings || {};
              const matchupLimits = currentSettings.matchupLimits || {};
              updateSettings('settings', {
                ...currentSettings,
                matchupLimits: {
                  ...matchupLimits,
                  seasonInningsCap: raw === '' ? null : parseInt(raw, 10)
                }
              });
            }}
            style={{ width: '200px', padding: '10px', fontSize: '16px' }}
          >
            <option value="1500">1500 IP</option>
            <option value="1250">1250 IP</option>
            <option value="">No cap</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Season-long cap on total team innings pitched; pitching stats beyond the cap don't count
          </small>
        </div>

        <div style={{
          marginTop: '20px',
          padding: '6px',
          backgroundColor: 'rgba(0, 113, 227, 0.08)',
          border: '1px solid rgba(0, 113, 227, 0.20)',
          borderRadius: '6px',
          fontSize: '13px',
          color: 'var(--primary)'
        }}>
          💡 <strong>Tips:</strong> Games Started limits prevent teams from streaming too many starting pitchers in head-to-head matchups. Minimum IP requirements ensure teams must use their pitching roster slots (typical minimum: 20-35 IP per week). Once limits are reached, additional stats won't count toward matchups.
        </div>
      </div>

    </div>
  );
}

// Transactions Settings Component
function TransactionsSettings({ league, updateSettings }) {
  const [transactions, setTransactions] = useState({
    waiverPeriod: league?.settings?.transactions?.waiverPeriod || "2",
    waiverType: league?.settings?.transactions?.waiverType || "rolling",
    faabBudget: league?.settings?.transactions?.faabBudget || 100,
    weeklyAcquisitionLimit: league?.settings?.transactions?.weeklyAcquisitionLimit || '',
    seasonAcquisitionLimit: league?.settings?.transactions?.seasonAcquisitionLimit || '',
    waiverProcessDay: league?.settings?.transactions?.waiverProcessDay || "wednesday",
    waiverProcessTime: league?.settings?.transactions?.waiverProcessTime || "03:00",
    tradeDeadline: league?.settings?.transactions?.tradeDeadline || '',
    tradeReviewPeriod: league?.settings?.transactions?.tradeReviewPeriod || "2",
    tradeReviewType: league?.settings?.transactions?.tradeReviewType || "league",
    tradeVotesToVeto: league?.settings?.transactions?.tradeVotesToVeto || 4,
    allowThreeTeamTrades: league?.settings?.transactions?.allowThreeTeamTrades || false,
    allowFuturePickTrades: league?.settings?.transactions?.allowFuturePickTrades || false
  });

  const handleTransactionChange = (key, value) => {
    const newTransactions = { ...transactions, [key]: value };
    setTransactions(newTransactions);
    updateSettings('transactions', newTransactions);
  };

  return (
    <div>
      <p style={{ color: 'var(--muted)', marginBottom: '30px' }}>
        Configure how players can be acquired and traded in your league
      </p>

      {/* Free Agents & Waiver Section */}
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <span style={{ fontSize: '20px' }}>👤</span>
          Free Agents & Waiver
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
          Control how teams acquire free agent players from the waiver wire
        </p>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Waiver Period
          </label>
          <select 
            className="form-control"
            value={transactions.waiverPeriod}
            onChange={(e) => handleTransactionChange('waiverPeriod', e.target.value)}
            style={{ width: '100%', padding: '10px', fontSize: '16px' }}
          >
            <option value="0">No Waivers (Free-For-All)</option>
            <option value="1">1 Day</option>
            <option value="2">2 Days</option>
            <option value="3">3 Days</option>
            <option value="7">1 Week</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            How long a dropped player stays on waivers before becoming a free agent
          </small>
        </div>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Waiver Type
          </label>
          <select 
            className="form-control"
            value={transactions.waiverType}
            onChange={(e) => handleTransactionChange('waiverType', e.target.value)}
            style={{ width: '100%', padding: '10px', fontSize: '16px' }}
          >
            <option value="rolling">Rolling (FAAB)</option>
            <option value="reverse">Reverse Standings Order</option>
            <option value="continual">Continual Rolling List</option>
            <option value="none">No Waiver Priority</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            <strong>FAAB:</strong> Free Agent Acquisition Budget - teams bid on players • <strong>Reverse Standings:</strong> Worst team gets first priority • <strong>Continual Rolling:</strong> Priority cycles through teams
          </small>
        </div>

        {transactions.waiverType === 'rolling' && (
          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              FAAB Budget
            </label>
            <input 
              type="number" 
              className="form-control"
              value={transactions.faabBudget}
              onChange={(e) => handleTransactionChange('faabBudget', parseInt(e.target.value) || 0)}
              min={0}
              max={1000}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Total budget each team gets for Free Agent Acquisition Budget (FAAB) bids (typical range: $100-200)
            </small>
          </div>
        )}

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Weekly Acquisition Limit
          </label>
          <input 
            type="number" 
            className="form-control"
            placeholder="Unlimited (leave blank or 0)"
            value={transactions.weeklyAcquisitionLimit || ''}
            onChange={(e) => handleTransactionChange('weeklyAcquisitionLimit', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
            min={0}
            max={25}
            style={{ width: '200px', padding: '10px', fontSize: '16px' }}
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Maximum number of acquisitions per team per week (leave blank or 0 for unlimited)
          </small>
        </div>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Season Acquisition Limit
          </label>
          <input 
            type="number" 
            className="form-control"
            placeholder="Unlimited (leave blank or 0)"
            value={transactions.seasonAcquisitionLimit || ''}
            onChange={(e) => handleTransactionChange('seasonAcquisitionLimit', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
            min={0}
            max={500}
            style={{ width: '200px', padding: '10px', fontSize: '16px' }}
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Maximum total acquisitions per team for the entire season (leave blank or 0 for unlimited)
          </small>
        </div>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Waiver Process Day & Time
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', maxWidth: '400px' }}>
            <select 
              className="form-control"
              value={transactions.waiverProcessDay}
              onChange={(e) => handleTransactionChange('waiverProcessDay', e.target.value)}
              style={{ padding: '10px', fontSize: '16px' }}
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="daily">Daily</option>
            </select>
            <input 
              type="time" 
              className="form-control"
              value={transactions.waiverProcessTime}
              onChange={(e) => handleTransactionChange('waiverProcessTime', e.target.value)}
              style={{ padding: '10px', fontSize: '16px' }}
            />
          </div>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            When waiver claims are processed each week (typically Wednesday at 3:00 AM EST)
          </small>
        </div>
      </div>

      {/* Trades Section */}
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <span style={{ fontSize: '20px' }}>🤝</span>
          Trades
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
          Control how teams can trade players with each other
        </p>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Trade Deadline
          </label>
          <input 
            type="date" 
            className="form-control"
            value={transactions.tradeDeadline}
            onChange={(e) => handleTransactionChange('tradeDeadline', e.target.value)}
            style={{ width: '100%', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Last day trades can be made (leave blank for no deadline, typically late August for MLB)
          </small>
        </div>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Trade Review Period
          </label>
          <select 
            className="form-control"
            value={transactions.tradeReviewPeriod}
            onChange={(e) => handleTransactionChange('tradeReviewPeriod', e.target.value)}
            style={{ width: '100%', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
          >
            <option value="0">Instant (No Review)</option>
            <option value="1">1 Day</option>
            <option value="2">2 Days (Recommended)</option>
            <option value="3">3 Days</option>
            <option value="7">1 Week</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            How long teams have to review and potentially veto a trade (2 days is standard)
          </small>
        </div>

        <div className="form-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Trade Review Type
          </label>
          <select 
            className="form-control"
            value={transactions.tradeReviewType}
            onChange={(e) => handleTransactionChange('tradeReviewType', e.target.value)}
            style={{ width: '100%', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
          >
            <option value="none">No Review (Instant Trades)</option>
            <option value="commissioner">Commissioner Review Only</option>
            <option value="league">League Vote (Recommended)</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Who can review and veto trades. League vote allows all teams to vote.
          </small>
        </div>

        {transactions.tradeReviewType === 'league' && (
          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Trade Votes Required to Veto
            </label>
            <input 
              type="number" 
              className="form-control"
              value={transactions.tradeVotesToVeto}
              onChange={(e) => handleTransactionChange('tradeVotesToVeto', parseInt(e.target.value) || 0)}
              min={0}
              max={16}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Number of votes needed to veto a trade (typically 4-6 votes in a 12-team league)
            </small>
          </div>
        )}

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={transactions.allowThreeTeamTrades}
              onChange={(e) => handleTransactionChange('allowThreeTeamTrades', e.target.checked)}
            />
            <span style={{ fontWeight: 'bold' }}>Allow 3+ Team Trades</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Enable trades involving more than two teams (e.g., Team A gets player from Team B, Team B gets player from Team C, etc.)
          </small>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={transactions.allowFuturePickTrades}
              onChange={(e) => handleTransactionChange('allowFuturePickTrades', e.target.checked)}
            />
            <span style={{ fontWeight: 'bold' }}>Allow Future Draft Pick Trading</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Enable trading of future draft picks (common in dynasty/keeper leagues)
          </small>
        </div>
      </div>

      <div style={{ 
        padding: '15px', 
        backgroundColor: 'rgba(255, 159, 10, 0.08)',
        border: '1px solid var(--warning)',
        borderRadius: '6px',
        fontSize: '14px',
        color: 'var(--foreground)'
      }}>
        💡 <strong>Tip:</strong> Most leagues use a 2-day trade review period with league voting, and 2-day waiver periods with reverse standings waiver priority. Adjust these settings to match your league's preferences.
      </div>
    </div>
  );
}

// Dynasty Settings Component
function DynastySettings({ league, updateSettings }) {
  const [dynastySettings, setDynastySettings] = useState({
    enabled: league?.settings?.dynastySettings?.enabled || false,
    maxKeepers: league?.settings?.dynastySettings?.maxKeepers || 5,
    keeperDeadline: league?.settings?.dynastySettings?.keeperDeadline || '',
    keeperCostType: league?.settings?.dynastySettings?.keeperCostType || 'none',
    maxYearsKept: league?.settings?.dynastySettings?.maxYearsKept || 'unlimited',
    salaryCapEnabled: league?.settings?.dynastySettings?.salaryCapEnabled || false,
    salaryCap: league?.settings?.dynastySettings?.salaryCap || 260,
    contractYearsEnabled: league?.settings?.dynastySettings?.contractYearsEnabled || false,
    maxContractYears: league?.settings?.dynastySettings?.maxContractYears || 5,
    minorLeagueKeepers: league?.settings?.dynastySettings?.minorLeagueKeepers || 0,
    rookieDraftEnabled: league?.settings?.dynastySettings?.rookieDraftEnabled || false,
    rookieDraftRounds: league?.settings?.dynastySettings?.rookieDraftRounds || 3
  });

  const handleSettingChange = (key, value) => {
    const newSettings = { ...dynastySettings, [key]: value };
    setDynastySettings(newSettings);
    updateSettings('dynastySettings', newSettings);
  };

  return (
    <div>
      <p style={{ color: 'var(--muted)', marginBottom: '20px', fontSize: '14px' }}>
        Configure dynasty/keeper league rules for maintaining players across seasons
      </p>

      {/* Basic Keeper Settings */}
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginBottom: '15px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <span style={{ fontSize: '20px' }}>🔒</span>
          Basic Keeper Settings
        </h3>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={dynastySettings.enabled}
              onChange={(e) => handleSettingChange('enabled', e.target.checked)}
            />
            <span style={{ fontWeight: 'bold' }}>Enable Dynasty/Keeper League</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Allow teams to keep players from one season to the next
          </small>
        </div>

        {dynastySettings.enabled && (
          <>
            <div className="form-group" style={{ marginTop: '15px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Maximum Keepers Per Team
              </label>
              <input 
                type="number" 
                className="form-control"
                value={dynastySettings.maxKeepers}
                onChange={(e) => handleSettingChange('maxKeepers', parseInt(e.target.value) || 0)}
                min={0}
                max={40}
                style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Maximum number of players each team can keep (0 = no keepers, full dynasty = up to 40)
              </small>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Keeper Deadline
              </label>
              <input 
                type="date" 
                className="form-control"
                value={dynastySettings.keeperDeadline}
                onChange={(e) => handleSettingChange('keeperDeadline', e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Date by which teams must declare their keepers for the upcoming season
              </small>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Keeper Cost Type
              </label>
              <select 
                className="form-control"
                value={dynastySettings.keeperCostType}
                onChange={(e) => handleSettingChange('keeperCostType', e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              >
                <option value="none">No Cost (Free Keepers)</option>
                <option value="round">Draft Round Penalty (Keep at -X rounds)</option>
                <option value="value">Dollar Value / Salary Cap</option>
                <option value="first-round">Must Use First Round Pick</option>
              </select>
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                How keeping a player affects draft position or budget
              </small>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Maximum Years a Player Can Be Kept
              </label>
              <select 
                className="form-control"
                value={dynastySettings.maxYearsKept}
                onChange={(e) => handleSettingChange('maxYearsKept', e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              >
                <option value="1">1 Year</option>
                <option value="2">2 Years</option>
                <option value="3">3 Years</option>
                <option value="4">4 Years</option>
                <option value="5">5 Years</option>
                <option value="unlimited">Unlimited (True Dynasty)</option>
              </select>
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Limit how many consecutive years a player can be kept
              </small>
            </div>
          </>
        )}
      </div>

      {/* Salary Cap & Contracts */}
      {dynastySettings.enabled && (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '6px', 
          borderRadius: '4px', 
          marginBottom: '15px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
            <span style={{ fontSize: '20px' }}>💰</span>
            Salary Cap & Contracts
          </h3>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={dynastySettings.salaryCapEnabled}
                onChange={(e) => handleSettingChange('salaryCapEnabled', e.target.checked)}
              />
              <span style={{ fontWeight: 'bold' }}>Enable Salary Cap</span>
            </label>
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
              Enforce a salary cap budget for each team
            </small>
          </div>

          {dynastySettings.salaryCapEnabled && (
            <div className="form-group" style={{ marginTop: '15px', marginLeft: '30px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Total Salary Cap Budget
              </label>
              <input 
                type="number" 
                className="form-control"
                value={dynastySettings.salaryCap}
                onChange={(e) => handleSettingChange('salaryCap', parseInt(e.target.value) || 0)}
                min={0}
                max={500}
                style={{ width: '200px', padding: '10px', fontSize: '16px' }}
              />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Total budget each team gets for player salaries (typical range: $260-300)
              </small>
            </div>
          )}

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={dynastySettings.contractYearsEnabled}
                onChange={(e) => handleSettingChange('contractYearsEnabled', e.target.checked)}
              />
              <span style={{ fontWeight: 'bold' }}>Enable Contract Years</span>
            </label>
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
              Allow teams to assign contract years to kept players
            </small>
          </div>

          {dynastySettings.contractYearsEnabled && (
            <div className="form-group" style={{ marginTop: '15px', marginLeft: '30px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Maximum Contract Years
              </label>
              <input 
                type="number" 
                className="form-control"
                value={dynastySettings.maxContractYears}
                onChange={(e) => handleSettingChange('maxContractYears', parseInt(e.target.value) || 1)}
                min={1}
                max={10}
                style={{ width: '200px', padding: '10px', fontSize: '16px' }}
              />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Maximum number of years a player can be signed to a contract
              </small>
            </div>
          )}
        </div>
      )}

      {/* Minor League & Rookie Draft */}
      {dynastySettings.enabled && (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '6px', 
          borderRadius: '4px', 
          marginBottom: '15px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
            <span style={{ fontSize: '20px' }}>🌟</span>
            Minor League & Rookie Draft
          </h3>

          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Minor League Keeper Spots
            </label>
            <input 
              type="number" 
              className="form-control"
              value={dynastySettings.minorLeagueKeepers}
              onChange={(e) => handleSettingChange('minorLeagueKeepers', parseInt(e.target.value) || 0)}
              min={0}
              max={20}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Number of minor league/prospect players each team can keep (separate from regular keepers)
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={dynastySettings.rookieDraftEnabled}
                onChange={(e) => handleSettingChange('rookieDraftEnabled', e.target.checked)}
              />
              <span style={{ fontWeight: 'bold' }}>Enable Annual Rookie Draft</span>
            </label>
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
              Conduct a separate rookie/prospect draft before the main draft
            </small>
          </div>

          {dynastySettings.rookieDraftEnabled && (
            <div className="form-group" style={{ marginTop: '15px', marginLeft: '30px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Rookie Draft Rounds
              </label>
              <input 
                type="number" 
                className="form-control"
                value={dynastySettings.rookieDraftRounds}
                onChange={(e) => handleSettingChange('rookieDraftRounds', parseInt(e.target.value) || 1)}
                min={1}
                max={10}
                style={{ width: '200px', padding: '10px', fontSize: '16px' }}
              />
              <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
                Number of rounds in the annual rookie/prospect draft
              </small>
            </div>
          )}
        </div>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: 'rgba(255, 159, 10, 0.08)',
        border: '1px solid var(--warning)',
        borderRadius: '6px',
        fontSize: '14px',
        color: 'var(--foreground)'
      }}>
        💡 <strong>Tip:</strong> Dynasty leagues allow teams to build and maintain rosters across multiple seasons. Keeper leagues typically allow 3-10 keepers, while true dynasty leagues allow keeping most or all players. Salary cap and contracts add complexity but create more strategic depth.
      </div>
    </div>
  );
}

// Draft Settings Component
function DraftSettings({ league, updateSettings, tabChanges }) {
  const effectiveDraftTime = tabChanges?.draftTime !== undefined ? tabChanges.draftTime : league?.draftTime;
  const [draftOrderType, setDraftOrderType] = useState(
    league?.settings?.draftSettings?.draftOrderType || 'random'
  );
  const [customDraftOrder, setCustomDraftOrder] = useState(
    league?.settings?.draftSettings?.customDraftOrder || 
    league.teams.filter(t => t.status !== 'invited').map(t => t.id)
  );

  const activeTeams = league.teams.filter(t => t.status !== 'invited');

  const moveTeamUp = (index) => {
    if (index === 0) return;
    const newOrder = [...customDraftOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setCustomDraftOrder(newOrder);
    if (updateSettings) {
      updateSettings('draftSettings', {
        ...league.settings?.draftSettings,
        customDraftOrder: newOrder,
        draftOrderType
      });
    }
  };

  const moveTeamDown = (index) => {
    if (index === customDraftOrder.length - 1) return;
    const newOrder = [...customDraftOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setCustomDraftOrder(newOrder);
    if (updateSettings) {
      updateSettings('draftSettings', {
        ...league.settings?.draftSettings,
        customDraftOrder: newOrder,
        draftOrderType
      });
    }
  };

  const randomizeDraftOrder = () => {
    const shuffled = [...customDraftOrder].sort(() => Math.random() - 0.5);
    setCustomDraftOrder(shuffled);
    if (updateSettings) {
      updateSettings('draftSettings', {
        ...league.settings?.draftSettings,
        customDraftOrder: shuffled,
        draftOrderType: 'manual'
      });
    }
  };

  const getTeamById = (teamId) => {
    return activeTeams.find(t => t.id === teamId);
  };

  return (
    <div>
      <div className="form-group">
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
          Draft Date
        </label>
        <input 
          type="date" 
          className="form-control"
          defaultValue={league?.draftDate}
          onChange={(e) => updateSettings && updateSettings('draftDate', e.target.value)}
          style={{ width: '100%', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
        />
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
          Date when the draft will take place
        </small>
      </div>

      <div className="form-group">
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
          Draft Time
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            aria-label="Draft hour"
            className="form-control"
            value={getDraftTimeParts(roundDraftTimeTo5Min(effectiveDraftTime)).hour}
            onChange={(e) => updateSettings && updateSettings('draftTime', formatDraftTime(Number(e.target.value), getDraftTimeParts(roundDraftTimeTo5Min(effectiveDraftTime)).minute))}
            style={{ width: 'auto', minWidth: '80px', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
            ))}
          </select>
          <span style={{ fontWeight: 'bold', color: 'var(--muted)' }}>:</span>
          <select
            aria-label="Draft minute"
            className="form-control"
            value={getDraftTimeParts(roundDraftTimeTo5Min(effectiveDraftTime)).minute}
            onChange={(e) => updateSettings && updateSettings('draftTime', formatDraftTime(getDraftTimeParts(roundDraftTimeTo5Min(effectiveDraftTime)).hour, Number(e.target.value)))}
            style={{ width: 'auto', minWidth: '80px', maxWidth: '400px', padding: '10px', fontSize: '16px' }}
          >
            {DRAFT_TIME_MINUTE_OPTIONS.map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
            ))}
          </select>
        </div>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
          Time when the draft will start. 5-minute intervals (e.g. 7:00, 7:05, 7:10). Timezone based on server settings.
        </small>
      </div>

      <div className="form-group">
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
          Draft Type
        </label>
        <select 
          className="form-control"
          defaultValue={league?.settings?.draftSettings?.draftType || 'snake'}
          onChange={(e) => updateSettings && updateSettings('draftSettings', {
            ...league.settings?.draftSettings,
            draftType: e.target.value
          })}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        >
          <option value="snake">Snake Draft (Recommended)</option>
          <option value="linear">Linear Draft</option>
          <option value="auction">Auction Draft</option>
          <option value="slow">Slow Draft (Email/Self-Paced)</option>
        </select>
        <small style={{ color: 'var(--muted)', fontSize: '13px', display: 'block', marginTop: '6px' }}>
          <strong>Snake:</strong> 1-2-3-4-4-3-2-1 (reverses each round) • <strong>Linear:</strong> 1-2-3-4-1-2-3-4 (same order each round) • <strong>Auction:</strong> Teams bid on players • <strong>Slow:</strong> Self-paced draft with extended time limits
        </small>
      </div>

      {/* Auction Draft Settings */}
      {(league?.settings?.draftSettings?.draftType === 'auction' || !league?.settings?.draftSettings?.draftType) && (
        <div style={{ 
          backgroundColor: 'rgba(0, 113, 227, 0.08)', 
          padding: '6px', 
          borderRadius: '4px', 
          marginTop: '15px',
          border: '1px solid #007bff'
        }}>
          <h4 style={{ marginBottom: '10px', fontSize: '14px', color: 'var(--primary)' }}>💰 Auction Draft Settings</h4>
          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Auction Budget Per Team
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.draftSettings?.auctionBudget || 260}
              min={100}
              max={500}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                auctionBudget: parseInt(e.target.value)
              })}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Total budget each team gets to bid on players (typical range: $260-300)
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Minimum Bid
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.draftSettings?.minimumBid || 1}
              min={1}
              max={10}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                minimumBid: parseInt(e.target.value)
              })}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Minimum bid amount for any player (typically $1)
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Nomination Time Limit (seconds)
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.draftSettings?.nominationTimeLimit || 60}
              min={10}
              max={300}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                nominationTimeLimit: parseInt(e.target.value)
              })}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Time limit for teams to nominate a player (typical range: 30-90 seconds)
            </small>
          </div>
        </div>
      )}

      {/* Slow Draft Settings */}
      {league?.settings?.draftSettings?.draftType === 'slow' && (
        <div style={{ 
          backgroundColor: 'rgba(255, 159, 10, 0.08)', 
          padding: '6px', 
          borderRadius: '4px', 
          marginTop: '15px',
          border: '1px solid var(--warning)'
        }}>
          <h4 style={{ marginBottom: '10px', fontSize: '14px', color: 'var(--foreground)' }}>⏰ Slow Draft Settings</h4>
          <div className="form-group">
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Hours Per Pick
            </label>
            <input 
              type="number" 
              className="form-control"
              defaultValue={league?.settings?.draftSettings?.hoursPerPick || 8}
              min={1}
              max={48}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                hoursPerPick: parseInt(e.target.value)
              })}
              style={{ width: '200px', padding: '10px', fontSize: '16px' }}
            />
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
              Hours each team has to make their pick (typical range: 4-12 hours)
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                defaultChecked={league?.settings?.draftSettings?.slowDraftPauseOvernight !== false}
                onChange={(e) => updateSettings && updateSettings('draftSettings', {
                  ...league.settings?.draftSettings,
                  slowDraftPauseOvernight: e.target.checked
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Pause Draft Overnight</span>
            </label>
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
              Pause the draft timer during overnight hours (e.g., 10 PM - 8 AM)
            </small>
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Number of Rounds</label>
        <input 
          type="number" 
          className="form-control"
          defaultValue={league?.settings?.draftSettings?.rounds || 25}
          min={10}
          max={40}
          onChange={(e) => updateSettings && updateSettings('draftSettings', {
            ...league.settings?.draftSettings,
            rounds: parseInt(e.target.value)
          })}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        />
      </div>

      <div className="form-group">
        <label>Seconds Per Pick</label>
        <input 
          type="number" 
          className="form-control"
          defaultValue={league?.settings?.draftSettings?.pickTimeLimit || 90}
          min={30}
          max={300}
          onChange={(e) => updateSettings && updateSettings('draftSettings', {
            ...league.settings?.draftSettings,
            pickTimeLimit: parseInt(e.target.value)
          })}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        />
      </div>

      <div className="form-group">
        <label>Draft Order Type</label>
        <select 
          className="form-control"
          value={draftOrderType}
          onChange={(e) => {
            setDraftOrderType(e.target.value);
            if (updateSettings) {
              updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                draftOrderType: e.target.value,
                customDraftOrder
              });
            }
          }}
          style={{ width: '100%', padding: '10px', fontSize: '16px' }}
        >
          <option value="random">Random Order</option>
          <option value="manual">Manually Set Order</option>
        </select>
      </div>

      {/* Manual Draft Order Configuration */}
      {draftOrderType === 'manual' && (
        <div className="form-group">
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Draft Order (First to Last)</span>
            <button 
              className="btn btn-secondary"
              onClick={randomizeDraftOrder}
              style={{ padding: '4px 12px', fontSize: '13px' }}
            >
              🎲 Randomize
            </button>
          </label>
          
          <div style={{ 
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '6px',
            backgroundColor: 'var(--surface-muted)',
            marginTop: '8px'
          }}>
            {customDraftOrder.map((teamId, index) => {
              const team = getTeamById(teamId);
              if (!team) return null;
              
              return (
                <div key={teamId} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px',
                  backgroundColor: 'var(--background)',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: team.logoColor || '#007bff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      color: 'var(--background)',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{
                      fontSize: '20px'
                    }}>
                      {team.logoEmoji || '⚾'}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        {team.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {team.ownerName}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => moveTeamUp(index)}
                      disabled={index === 0}
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '12px',
                        opacity: index === 0 ? 0.3 : 1
                      }}
                    >
                      ▲
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => moveTeamDown(index)}
                      disabled={index === customDraftOrder.length - 1}
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '12px',
                        opacity: index === customDraftOrder.length - 1 ? 0.3 : 1
                      }}
                    >
                      ▼
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ 
            marginTop: '12px',
            padding: '6px',
            backgroundColor: 'rgba(0, 113, 227, 0.08)',
            border: '1px solid rgba(0, 113, 227, 0.20)',
            borderRadius: '6px',
            fontSize: '13px',
            color: 'var(--primary)'
          }}>
            ℹ️ <strong>Draft Order Preview:</strong> In a snake draft, this is the order for round 1. 
            Round 2 will reverse (last to first), round 3 goes back to normal, and so on.
          </div>
        </div>
      )}

      <div className="form-group">
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
          Draft Timer & Auto-Pick
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
          <input 
            type="checkbox" 
            defaultChecked={league?.settings?.draftSettings?.autoPickEnabled !== false}
            onChange={(e) => updateSettings && updateSettings('draftSettings', {
              ...league.settings?.draftSettings,
              autoPickEnabled: e.target.checked
            })}
          />
          <span>Enable Auto-Pick (when timer expires)</span>
        </label>
        <small style={{ color: 'var(--muted)', display: 'block', marginLeft: '30px', marginBottom: '10px' }}>
          Automatically pick the highest-ranked available player if timer expires
        </small>

        <div className="form-group" style={{ marginLeft: '30px' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
            Auto-Pick Strategy
          </label>
          <select 
            className="form-control"
            defaultValue={league?.settings?.draftSettings?.autoPickStrategy || 'best-available'}
            onChange={(e) => updateSettings && updateSettings('draftSettings', {
              ...league.settings?.draftSettings,
              autoPickStrategy: e.target.value
            })}
            style={{ width: '100%', padding: '10px', fontSize: '16px', maxWidth: '400px' }}
          >
            <option value="best-available">Best Available (Overall)</option>
            <option value="fill-roster">Fill Roster Needs</option>
            <option value="position-balance">Position Balance</option>
            <option value="ignore-catcher">Ignore Catcher (fill other positions first)</option>
          </select>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px' }}>
            Strategy for auto-picking when timer expires
          </small>
        </div>
      </div>

      {/* Draft Pick Trading */}
      <div style={{ 
        backgroundColor: 'var(--surface-muted)', 
        padding: '6px', 
        borderRadius: '4px', 
        marginTop: '20px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>🔄 Draft Pick Trading</h3>
        
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              defaultChecked={league?.settings?.draftSettings?.allowPickTrading || false}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                allowPickTrading: e.target.checked
              })}
            />
            <span style={{ fontWeight: 'bold' }}>Allow Draft Pick Trading</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Allow teams to trade draft picks during or before the draft
          </small>
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              defaultChecked={league?.settings?.draftSettings?.allowFuturePickTrading || false}
              onChange={(e) => updateSettings && updateSettings('draftSettings', {
                ...league.settings?.draftSettings,
                allowFuturePickTrading: e.target.checked
              })}
            />
            <span style={{ fontWeight: 'bold' }}>Allow Future Year Pick Trading</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
            Enable trading of draft picks from future seasons (dynasty leagues)
          </small>
        </div>
      </div>

      <div style={{ 
        marginTop: '30px',
        padding: '16px',
        backgroundColor: 'rgba(255, 159, 10, 0.08)',
        border: '1px solid var(--warning)',
        borderRadius: '8px'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: 'var(--foreground)' }}>⚠️ Before Starting Draft</h4>
        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--foreground)', fontSize: '14px' }}>
          <li>Ensure all teams have joined the league</li>
          <li>Set the draft order above</li>
          <li>Save these settings before starting</li>
          <li>Once started, draft order cannot be changed</li>
        </ul>
      </div>
    </div>
  );
}

export default LeagueSettings;